import React, { useState, useRef, useEffect, startTransition } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import styled from 'styled-components';
import { AnalyticsTracker, useAnalyticsEvent } from '../../components/AnalyticsWrapper';
import dizeeLogo from '../../assets/images/dizee logo.svg';
import DizeeWalkthrough from '../../components/DizeeWalkthrough';
import DizeeTutorial from '../../components/DizeeTutorial';
import { clearAlerts } from '../../store/alert/alertSlice';
import { logoutThunk } from '../../store/auth/authThunk';
import { get_user_articles_thunk } from '../../store/user/userThunk';
import { setUserArticle } from '../../store/user/userSlice';
import Line2 from '../../svg/Line2';
import { setWholeFocusCustom, setWholeFocusEvent, setWholeFocusMusic, setWholeFocusNewsletter, setWholeFocusProduct } from '../../store/focuspage/focuspageSlice';
import FooterBar from '../../components/FooterBar';
import HeaderBar from '../../components/HeaderBar';
import DashboardStats from '../../components/DashboardStats';
import autoPopulationService from '../../services/autoPopulationService';
import { getDataAPI, postDataAPI, putDataAPI } from '../../utils/fetchData';
import CustomCalendar from '../../components/Calendar/CustomCalendar';
import { getTeamMembersForTasks } from '../../utils/approvalUtils';
import PermissionWrapper from '../../components/Permissions/PermissionWrapper';
import OrangeOrb3D from '../../components/Homepage/OrangeOrb3D';
import AppsExpanded from '../../components/AppsExpanded';
import { isCurrentUserDemo } from '../../utils/demoUserConfig';
import { motion } from 'framer-motion';
import { TourOptions } from '../../components/InteractiveTour/TourOptions';
import { TourPresetQuestions } from '../../components/InteractiveTour/TourPresetQuestions';
import {
  TOUR_SECTIONS,
  TOUR_CONTENT,
  QUICK_RESPONSES,
  QUESTION_ANSWERS,
  isTourActive,
  setTourActive,
  setCurrentTourSection,
  completeTour,
  getWelcomeMessage,
  getPostChecklistWelcomeMessage,
  getSectionWelcomeMessage,
  saveTourMessages,
  loadTourMessages,
  clearTourMessages,
  isChecklistCompleted
} from '../../utils/interactiveTour';
import { checkUserData } from '../../utils/onboardingStateMachine';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  min-height: 100vh;
  width: 100%;
  margin: 0 auto;
  overflow: hidden;
  background-color: black;
  position: relative;
  touch-action: none;
  
  @media (max-width: 768px) {
    overflow: hidden;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    min-height: 100vh;
  }
`;

const MainContent = styled.div`
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  max-width: 390px;
  margin: 0 auto;
  width: 100%;
  overflow: visible;
  position: relative;
  padding-top: 120px;
  min-height: 0;
  touch-action: none;
  height: calc(100vh - 80px);
  max-height: calc(100vh - 80px);
  
  @media (max-width: 768px) {
    padding-top: 120px;
    overflow: visible;
    touch-action: none;
    min-height: 0;
    height: calc(100vh - 80px);
    max-height: calc(100vh - 80px);
  }
`;

const OptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  width: ${props => props.isActivityTab ? '100%' : '90%'};
  margin: 0 auto;
  overflow: ${props => props.isActivityTab ? 'hidden' : 'visible'};
  flex: 1 1 auto;
  min-height: 0;
  touch-action: none;
  
  @media (max-width: 768px) {
    width: ${props => props.isActivityTab ? '100%' : '90%'};
    overflow: ${props => props.isActivityTab ? 'hidden' : 'visible'};
    touch-action: none;
    min-height: 0;
  }
`;

const WelcomeMessage = styled.div`
  font-size: 16px;
  color: white;
  margin-top: 0px; /* Reduced from 100px */
  margin-bottom: 20px;
  text-align: left;
  font-weight: 500;
`;

const AppsSection = styled.div`
  margin-bottom: 15px;
  margin-top: 40px;
`;

const AppsContainer = styled.div`
  position: relative;
  margin-bottom: 15px;
`;

const AppsScrollContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 60px);
  justify-content: space-between;
  row-gap: 20px;
  padding: 0 0%;
  /* Use a reasonable max-height that adapts to zoom */
  max-height: 60vh;
  overflow-y: auto !important;
  overflow-x: hidden !important;
  scrollbar-width: none;
  -ms-overflow-style: none;
  -webkit-overflow-scrolling: touch;
  position: relative;
  min-height: 0;
  
  &::-webkit-scrollbar {
    display: none;
  }
`;

const AppWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 60px;
`;

const AppIcon = styled.div`
  width: 60px;
  height: 60px;
  background-color: ${props => props.color};
  border-radius: 12px;
  cursor: pointer;
  transition: transform 0.2s ease;
  
  &:active {
    transform: scale(0.95);
  }
`;

const AppLabel = styled.div`
  margin-top: 8px;
  font-size: 10px;
  color: white;
  text-align: center;
  line-height: 1.2;
`;

const AppsFooter = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0 10px;
  position: relative;
`;

const PaginationDots = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
`;

const Dot = styled.div`
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background-color: ${props => props.active ? 'white' : 'rgba(255, 255, 255, 0.3)'};
  transition: background-color 0.2s ease;
  cursor: pointer;
  
  &:hover {
    background-color: ${props => props.active ? 'white' : 'rgba(255, 255, 255, 0.6)'};
  }
`;

const MoreAppsLink = styled.button`
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  position: absolute;
  right: 10px;
  
  &:hover {
    color: white;
  }
`;

const CollapseToggle = styled.button`
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  position: absolute;
  left: 10px;
  
  &:hover {
    color: white;
  }
`;

const ReminderText = styled.span`
  font-size: 12px;
  font-weight: normal;
  color: rgba(255, 255, 255, 0.7);
`;

const MainTabButton = styled.button`
  flex: 1;
  background: transparent;
  border: none;
  color: ${props => props.active ? 'white' : 'rgba(255, 255, 255, 0.5)'};
  font-size: 14px;
  font-weight: 300;
  cursor: pointer;
  padding: 0;
  text-align: ${props => props.align || 'center'};
  transition: all 0.2s ease;
  
  &:hover {
    color: white;
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

const ClickToChatText = styled.div`
  position: fixed;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  max-width: 390px;
  color: white;
  font-size: 12px;
  font-weight: 400;
  cursor: pointer;
  z-index: 5;
  transition: none;
  background: #000000;
  padding: 12px 0;
  border-radius: 6px 6px 0 0;
  text-align: center;
  
  &:hover {
    background: #000000;
  }
`;

const ChatContainer = styled(motion.div)`
  width: 100%;
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  gap: ${props => props.isMaximized ? '0' : '15px'};
  padding: ${props => props.isMaximized ? '20px 0 0 0' : '0'};
  overflow: visible;
  position: relative;
  min-height: 0;
  touch-action: none;
  transition: gap 0.3s cubic-bezier(0.4, 0, 0.2, 1), padding 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  will-change: padding, gap;
  
  @media (max-width: 768px) {
    padding: ${props => props.isMaximized ? '10px 0 0 0' : '0'};
    gap: ${props => props.isMaximized ? '0' : '10px'};
    overflow: visible;
    touch-action: none;
    min-height: 0;
  }
`;

const OrbWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  position: relative;
  overflow: visible;
  padding: 0;
  margin-top: 60px;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    padding: 0;
    margin-top: 50px;
    flex-shrink: 0;
  }
  
  &::before {
    content: '';
    position: fixed;
    left: 50%;
    top: 45%;
    transform: translate(-50%, -50%);
    width: ${props => props.isTyping ? '700px' : '500px'};
    height: ${props => props.isTyping ? '700px' : '500px'};
    background: ${props => props.isTyping 
      ? 'radial-gradient(circle, rgba(255, 35, 0, 0.5) 0%, rgba(255, 35, 0, 0.3) 20%, rgba(255, 35, 0, 0.15) 35%, rgba(255, 35, 0, 0.05) 50%, transparent 65%)'
      : 'radial-gradient(circle, rgba(255, 35, 0, 0.05) 0%, rgba(255, 35, 0, 0.02) 25%, transparent 50%)'
    };
    filter: ${props => props.isTyping ? 'blur(60px)' : 'blur(20px)'};
    z-index: 1001;
    pointer-events: none;
    transition: all 6s cubic-bezier(0.2, 0, 0.1, 1);
    ${props => props.isTyping && `
      animation: glowPulse 1.2s ease-in-out infinite;
    `}
    
    @media (max-width: 768px) {
      top: 32%;
      width: ${props => props.isTyping ? '500px' : '400px'};
      height: ${props => props.isTyping ? '500px' : '400px'};
    }
  }
  
  @keyframes glowPulse {
    0%, 100% {
      opacity: 1;
      top: 45%;
      transform: translate(-50%, -50%) scale(1);
      filter: blur(60px);
    }
    25% {
      opacity: 1.2;
      top: 45%;
      transform: translate(-50%, -50%) scale(1.05);
      filter: blur(65px);
    }
    50% {
      opacity: 1.3;
      top: 45%;
      transform: translate(-50%, -50%) scale(1.08);
      filter: blur(62px);
    }
    75% {
      opacity: 1.15;
      top: 45%;
      transform: translate(-50%, -50%) scale(1.03);
      filter: blur(62px);
    }
    
    @media (max-width: 768px) {
      0%, 100% {
        opacity: 1;
        top: 32%;
        transform: translate(-50%, -50%) scale(1);
        filter: blur(60px);
      }
      25% {
        opacity: 1.2;
        top: 32%;
        transform: translate(-50%, -50%) scale(1.05);
        filter: blur(65px);
      }
      50% {
        opacity: 1.3;
        top: 32%;
        transform: translate(-50%, -50%) scale(1.08);
        filter: blur(62px);
      }
      75% {
        opacity: 1.15;
        top: 32%;
        transform: translate(-50%, -50%) scale(1.03);
        filter: blur(62px);
      }
    }
  }
`;

const BottomGlow = styled.div`
  position: fixed;
  bottom: -150px;
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  max-width: 390px;
  height: 300px;
  background: radial-gradient(ellipse at center bottom, rgba(255, 35, 0, 0.5) 0%, rgba(255, 35, 0, 0.3) 25%, rgba(255, 35, 0, 0.15) 50%, rgba(255, 35, 0, 0.05) 75%, transparent 100%);
  filter: blur(40px);
  z-index: 10000;
  pointer-events: none;
  opacity: ${props => props.isVisible ? 1 : 0};
  transition: opacity 2s cubic-bezier(0.2, 0, 0.1, 1);
  
  ${props => props.isVisible && `
    animation: bottomGlowPulse 2s ease-in-out infinite;
  `}
  
  @keyframes bottomGlowPulse {
    0%, 100% {
      opacity: 1;
      filter: blur(40px);
    }
    50% {
      opacity: 1.2;
      filter: blur(45px);
    }
  }
`;

const OrbContainer = styled(motion.div)`
  width: 280px;
  height: 280px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: visible;
  z-index: 1;
  pointer-events: none;
  
  @media (max-width: 768px) {
    width: 200px;
    height: 200px;
  }
`;

const ConversationArea = styled.div`
  width: 100%;
  max-width: 100%;
  /* Create scroll area in both maximized and normal views */
  flex: 1 1 0;
  max-height: ${props => props.isMaximized ? 'none' : '400px'};
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 20px;
  padding-bottom: 20px;
  overflow-y: auto !important;
  overflow-x: hidden !important;
  position: relative;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  touch-action: pan-y;
  transition: max-height 0.2s ease;
  
  scrollbar-width: none;
  -ms-overflow-style: none;
  
  &::-webkit-scrollbar {
    display: none;
  }
  
  @media (max-width: 768px) {
    flex: 1 1 0;
    max-height: ${props => props.isMaximized ? 'none' : '350px'};
    gap: 20px;
    padding: 16px;
    padding-bottom: 16px;
    overflow-y: auto !important;
    overflow-x: hidden !important;
    -webkit-overflow-scrolling: touch;
    touch-action: pan-y !important;
    min-height: 0 !important;
  }
`;

/* Spacer to ensure content is visible above input area - uses viewport units to adapt to zoom */
const ChatSpacer = styled.div`
  flex-shrink: 0;
  /* Use vh units so it scales with zoom and screen size */
  height: ${props => props.isMaximized ? '35vh' : '30vh'};
  min-height: ${props => props.isMaximized ? '200px' : '150px'};
  width: 100%;
  transition: height 0.2s ease, min-height 0.2s ease;
`;

const AIMessage = styled(motion.div)`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.85);
  align-self: ${props => props.role === 'user' ? 'flex-end' : 'flex-start'};
  max-width: 75%;
  text-align: ${props => props.role === 'user' ? 'right' : 'left'};
  word-wrap: break-word;
  overflow-wrap: break-word;
  word-break: break-word;
  white-space: pre-wrap;
  
  .orange-bullet {
    color: #ff2300;
    font-weight: 600;
  }
`;

const NavigationMessage = styled(motion.div)`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.6;
  color: #8E8E93;
  align-self: flex-start;
  max-width: 85%;
  text-align: left;
  word-wrap: break-word;
  overflow-wrap: break-word;
  word-break: break-word;
  white-space: pre-wrap;
`;

const ThinkingMessage = styled(motion.div)`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.6);
  align-self: flex-start;
  display: flex;
  align-items: center;
  gap: 2px;
  
  .dot {
    opacity: 0;
    animation: dotFade 1.4s infinite;
  }
  
  .dot:nth-child(2) {
    animation-delay: 0.2s;
  }
  
  .dot:nth-child(3) {
    animation-delay: 0.4s;
  }
  
  @keyframes dotFade {
    0%, 60%, 100% {
      opacity: 0;
    }
    30% {
      opacity: 1;
    }
  }
`;

const Landing = () => {
  // Helper function to format text with bold support (Discord-style **)
  const formatTextWithBold = (text) => {
    if (!text) return text;
    
    // Split by ** to find bold sections
    const parts = text.split(/(\*\*.*?\*\*)/g);
    
    return parts.map((part, idx) => {
      // Check if this part is wrapped in **
      if (part.startsWith('**') && part.endsWith('**')) {
        // Remove the ** and return bold text
        const boldText = part.slice(2, -2);
        return <strong key={idx} style={{ fontWeight: '600', color: 'rgba(255, 255, 255, 0.95)' }}>{boldText}</strong>;
      }
      return part;
    });
  };

  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [showWalkthrough, setShowWalkthrough] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [tutorialStep, setTutorialStep] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [isDashboardCollapsed, setIsDashboardCollapsed] = useState(() => {
    // Load collapse state from localStorage, default to true (collapsed) to show AI chat
    const saved = localStorage.getItem('dashboardCollapsed');
    return saved ? JSON.parse(saved) : true;
  });
  // Add activeTab state - default to 'chat' per the new design
  const [activeTab, setActiveTab] = useState(() => {
    const saved = localStorage.getItem('activeHomeTab');
    return saved || 'chat';
  });
  // Add expanded apps view state
  const [isAppsExpanded, setIsAppsExpanded] = useState(() => {
    const saved = localStorage.getItem('appsViewExpanded');
    return saved ? JSON.parse(saved) : false;
  });
  const [activeNavItem, setActiveNavItem] = useState(null); // For category filtering
  const [chatMessages, setChatMessages] = useState([]); // Chat messages
  const [isTyping, setIsTyping] = useState(false); // Typing state
  const [typingBuffer, setTypingBuffer] = useState(''); // Currently typing text
  const [isThinking, setIsThinking] = useState(false); // Thinking/loading state
  const [isChatMaximized, setIsChatMaximized] = useState(false); // Chat maximized state
  const [isFadingOut, setIsFadingOut] = useState(false); // Track if chat is fading out for new chat
  const [userHasScrolledUp, setUserHasScrolledUp] = useState(false); // Track if user manually scrolled
  const [currentTourSectionState, setCurrentTourSectionState] = useState(null); // Tour section state
  const [isNavigatingTour, setIsNavigatingTour] = useState(false); // Tour navigation state
  const [userData, setUserData] = useState({ catalogHasData: false, bookingsHasData: false }); // User data state for tour
  
  // CRITICAL: Declare refs BEFORE useState that uses them
  const checklistLoadedRef = useRef(false);
  const isLoadingChecklistRef = useRef(false);
  
  // Setup checklist state
  const [checklistItems, setChecklistItems] = useState(() => {
    // Load from localStorage on initial mount
    const saved = localStorage.getItem('setupChecklistItems');
    const parsed = saved ? JSON.parse(saved) : [];
    console.log('📋 Initial checklist state from localStorage:', parsed);
    // Mark as loaded immediately so save effect knows we've initialized
    checklistLoadedRef.current = true;
    return parsed;
  });
  
  // Checklist items configuration
  const CHECKLIST_ITEMS_CONFIG = [
    { 
      id: 'past-releases', 
      label: 'Add Past Releases', 
      route: '/catalog',
      description: 'Add your existing catalog of released music'
    },
    { 
      id: 'upcoming-release', 
      label: 'Add Upcoming Release', 
      route: '/catalog',
      description: 'Add any upcoming releases you have planned'
    },
    { 
      id: 'live-bookings', 
      label: 'Add Live Bookings', 
      route: '/bookings',
      description: 'Add your upcoming shows and gigs'
    },
    { 
      id: 'explore-tour-manager', 
      label: 'Explore Tour Manager', 
      route: '/tour-manager/view-all',
      description: 'Check out the tour planning features'
    },
    { 
      id: 'explore-workflows', 
      label: 'Explore Workflows', 
      route: '/flows',
      description: 'See how automation can help you'
    },
    { 
      id: 'add-team-members', 
      label: 'Add Team Members', 
      route: '/team-management',
      description: 'Invite your team to collaborate'
    },
    { 
      id: 'add-tasks-approvals', 
      label: 'Add Tasks & Approvals', 
      route: '/tasks',
      description: 'Set up your task management system'
    }
  ];
  
  // Confirmed shows for ticket counts form
  const [confirmedShows, setConfirmedShows] = useState([]);
  const [showTicketCountsForm, setShowTicketCountsForm] = useState(false);
  const [isLoadingConfirmedShows, setIsLoadingConfirmedShows] = useState(false);

  // Reset confirmed shows cache
  const resetConfirmedShowsCache = () => {
    setConfirmedShows([]);
    setShowTicketCountsForm(false);
    setIsLoadingConfirmedShows(false);
  };

  // Catalog form state
  const [catalogFormData, setCatalogFormData] = useState({
    songTitle: '',
    primaryArtists: [''],
    featuredArtists: [''],
    releaseStatus: 'upcoming',
    releaseDate: '',
    additionalInfo: ''
  });
  const [catalogFormType, setCatalogFormType] = useState(null); // 'release' or 'demo'
  const [isSubmittingForm, setIsSubmittingForm] = useState(false);
  const [currentConversationId, setCurrentConversationId] = useState(null); // Track current conversation ID
  
  // Booking form state
  const [bookingFormData, setBookingFormData] = useState({
    venue: '',
    artist: '',
    perfDate: '',
    city: '',
    state: '',
    additionalInfo: ''
  });
  const [bookingFormType, setBookingFormType] = useState(null); // 'show'
  const [isSubmittingBookingForm, setIsSubmittingBookingForm] = useState(false);
  
  // Task form state
  const [taskFormData, setTaskFormData] = useState({
    title: '',
    description: '',
    due_date: '',
    priority: 'medium',
    assignee: '',
    auto_reminder: 'on_due_date'
  });
  const [taskFormType, setTaskFormType] = useState(null); // 'new'
  const [isSubmittingTaskForm, setIsSubmittingTaskForm] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loadingTeam, setLoadingTeam] = useState(false);
  
  // Confirm shows checklist state
  const [pendingShowsData, setPendingShowsData] = useState([]);
  const [selectedShowIds, setSelectedShowIds] = useState([]);
  const [isSubmittingConfirmation, setIsSubmittingConfirmation] = useState(false);
  
  const conversationRef = useRef(null);
  const profilePicRef = useRef(null);
  const dropdownRef = useRef(null);
  const appsScrollRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [user, setUser] = useState(JSON.parse(localStorage.getItem('dizeeUser')));
  
  // Save checklist items to localStorage whenever they change
  // CRITICAL: Never save during loading or with empty arrays
  useEffect(() => {
    // Don't save if we're currently loading
    if (isLoadingChecklistRef.current) {
      console.log('⏸️ Skipping save - currently loading checklist');
      return;
    }
    
    // CRITICAL FIX: If state is empty but localStorage has data, load from localStorage instead
    if (checklistItems.length === 0 && checklistLoadedRef.current) {
      const saved = localStorage.getItem('setupChecklistItems');
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          if (parsed.length > 0) {
            console.log('🔄 State is empty but localStorage has data - loading from localStorage:', parsed);
            isLoadingChecklistRef.current = true;
            setChecklistItems(parsed);
            setTimeout(() => {
              isLoadingChecklistRef.current = false;
            }, 50);
            return;
          }
        } catch (e) {
          console.error('❌ Error checking localStorage:', e);
        }
      }
      console.log('⚠️ Checklist is empty and no saved data - not saving empty array');
      return;
    }
    
    // Only save if we have items AND we've already loaded (to prevent overwriting with empty on mount)
    if (checklistItems.length > 0 && checklistLoadedRef.current) {
      // Check if what we're about to save is different from what's in localStorage
      const currentSaved = localStorage.getItem('setupChecklistItems');
      if (currentSaved) {
        try {
          const savedParsed = JSON.parse(currentSaved);
          const savedSorted = [...savedParsed].sort();
          const currentSorted = [...checklistItems].sort();
          if (JSON.stringify(savedSorted) === JSON.stringify(currentSorted)) {
            console.log('⏭️ Checklist state matches localStorage - skipping save');
            return;
          }
        } catch (e) {
          // If we can't parse, proceed with save
        }
      }
      
      localStorage.setItem('setupChecklistItems', JSON.stringify(checklistItems));
      console.log('💾 Saving checklist items to localStorage:', checklistItems);
    }
  }, [checklistItems]);

  // Load checklist state whenever we're on the landing page
  // This ensures state persists when navigating away and back
  useEffect(() => {
    if (location.pathname === '/') {
      // Set loading flag to prevent save effect from running
      isLoadingChecklistRef.current = true;
      
      // Load immediately when on landing page
      const saved = localStorage.getItem('setupChecklistItems');
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          console.log('🔄 [LANDING PAGE] Loading checklist from localStorage:', parsed);
          // Mark as loaded BEFORE updating state to prevent save effect from overwriting
          checklistLoadedRef.current = true;
          // Force update state from localStorage
          setChecklistItems(parsed);
        } catch (e) {
          console.error('❌ Error loading checklist:', e);
        }
      } else {
        // No saved data, mark as loaded so we can save future changes
        checklistLoadedRef.current = true;
      }
      
      // Clear loading flag after a brief delay to allow state to update
      setTimeout(() => {
        isLoadingChecklistRef.current = false;
      }, 100);
    }
  }, [location.pathname]); // Run whenever pathname changes (including returning to landing page)

  // Restore tour state and checklist when returning to landing page in tour mode
  useEffect(() => {
    // Only run when on landing page
    if (location.pathname !== '/') return;
    
    const tourActive = isTourActive();
    const checklistCompleted = isChecklistCompleted();
    
    // Also load checklist when tour is active (backup loader)
    if (tourActive) {
      const saved = localStorage.getItem('setupChecklistItems');
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          console.log('🔄 [TOUR ACTIVE] Loading checklist from localStorage:', parsed);
          // Mark as loaded BEFORE updating state
          checklistLoadedRef.current = true;
          setChecklistItems(parsed);
        } catch (e) {
          console.error('❌ Error parsing checklist items:', e);
        }
      }
    }
    
    // If tour is active and we're on the landing page, restore tour state
    if (tourActive) {
      // PRIORITY: If checklist is not completed, always show checklist (not saved tour messages)
      if (!checklistCompleted) {
        setChatMessages(prev => {
          const hasChecklist = prev.some(m => m.isChecklistMessage);
          if (!hasChecklist) {
            const checklistMessage = {
              role: 'assistant',
              text: `Welcome to DIZEE! Let's get your account set up.

I'll guide you through a quick checklist to make sure everything is ready to go.`,
              isTourMessage: true,
              isChecklistMessage: true
            };
            return [checklistMessage];
          }
          return prev;
        });
        setIsChatMaximized(true);
        setActiveTab('chat');
      } else {
        // Checklist is completed, restore saved tour messages or show welcome
        const savedMessages = loadTourMessages();
        if (savedMessages && savedMessages.length > 0) {
          // Filter out checklist messages, section-specific messages, and navigation messages
          // These shouldn't be on landing page - only show main tour messages
          const filteredMessages = savedMessages.filter(m => 
            !m.isChecklistMessage && 
            !m.showCurrentSection && 
            !m.isInSection &&
            !m.isNavigationMessage
          );
          
          if (filteredMessages.length > 0) {
            // Update messages if different
            setChatMessages(prev => {
              if (JSON.stringify(prev) !== JSON.stringify(filteredMessages)) {
                return filteredMessages;
              }
              return prev;
            });
            setIsChatMaximized(true);
            setActiveTab('chat');
          } else {
            // No valid messages after filtering, show post-checklist welcome
            const welcome = getPostChecklistWelcomeMessage();
            setChatMessages([welcome]);
            setIsChatMaximized(true);
            setActiveTab('chat');
          }
        } else {
          // No saved messages, show post-checklist welcome
          setChatMessages(prev => {
            if (prev.length === 0 || prev.some(m => m.isChecklistMessage || m.showCurrentSection || m.isInSection || m.isNavigationMessage)) {
              const welcome = getPostChecklistWelcomeMessage();
              return [welcome];
            }
            return prev;
          });
          setIsChatMaximized(true);
          setActiveTab('chat');
        }
      }
    }
  }, [location.pathname]); // Run when pathname changes (including browser back)
  
  // Check if we should restart tour on load (from "Back to Chat" button)
  useEffect(() => {
    const shouldRestart = sessionStorage.getItem('restartTourOnLoad');
    if (shouldRestart === 'true') {
      console.log('🔄 Restarting tour from Back to Chat...');
      
      // Clear saved tour messages for a fresh start
      clearTourMessages();
      
      // Clear checklist completion to show checklist again
      localStorage.removeItem('setupChecklistCompleted');
      // Only clear checklist items if explicitly restarting (not just navigating back)
      localStorage.removeItem('setupChecklistItems');
      // Reset refs to prevent save effect from running
      checklistLoadedRef.current = false;
      isLoadingChecklistRef.current = false;
      setChecklistItems([]);
      
      // Clear the restart flag so it doesn't run again
      sessionStorage.removeItem('restartTourOnLoad');
      
      // Check if checklist should be shown
      const checklistCompleted = isChecklistCompleted();
      
      if (!checklistCompleted) {
        // Show checklist message
        const checklistMessage = {
          role: 'assistant',
          text: `Welcome to DIZEE! Let's get your account set up.

I'll guide you through a quick checklist to make sure everything is ready to go.`,
          isTourMessage: true,
          isChecklistMessage: true
        };
        setChatMessages([checklistMessage]);
      } else {
        // Show post-checklist welcome
        const welcome = getPostChecklistWelcomeMessage();
      setChatMessages([welcome]);
      }
      
      // Ensure chat tab is active and maximized
      setActiveTab('chat');
      setIsDashboardCollapsed(true);
      
      // Use setTimeout to ensure state updates have propagated
      setTimeout(() => {
        setIsChatMaximized(true);
      }, 100);
      
      sessionStorage.removeItem('restartTourOnLoad'); // Clear the flag
    }
  }, []);

  // Listen for conversation ID updates from FooterBar
  useEffect(() => {
    const handleConversationCreated = (event) => {
      const conversationId = event.detail?.conversationId;
      if (conversationId) {
        console.log('📝 Landing: Received conversation ID:', conversationId);
        setCurrentConversationId(conversationId);
      }
    };

    const handleConversationUpdated = (event) => {
      const conversationId = event.detail?.conversationId;
      if (conversationId) {
        console.log('📝 Landing: Updated conversation ID:', conversationId);
        setCurrentConversationId(conversationId);
      }
    };

    window.addEventListener('newConversationCreated', handleConversationCreated);
    window.addEventListener('conversationUpdated', handleConversationUpdated);

    return () => {
      window.removeEventListener('newConversationCreated', handleConversationCreated);
      window.removeEventListener('conversationUpdated', handleConversationUpdated);
    };
  }, []);

  // Smart auto-scroll: only scroll if user hasn't manually scrolled up
  useEffect(() => {
    if (conversationRef.current && !userHasScrolledUp) {
      const scrollContainer = conversationRef.current;
      // Use double requestAnimationFrame to ensure spacer is rendered
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          scrollContainer.scrollTop = scrollContainer.scrollHeight;
        });
      });
    }
  }, [chatMessages, typingBuffer, isThinking, isChatMaximized, userHasScrolledUp]);
  
  // Keep chat maximized on desktop (like mobile behavior)
  useEffect(() => {
    const checkAndMaximize = () => {
      const isDesktop = typeof window !== 'undefined' && window.innerWidth >= 768;
      if (isDesktop && (chatMessages.length > 0 || typingBuffer || isThinking) && !isChatMaximized) {
        setIsChatMaximized(true);
      }
    };
    
    // Check on mount and when dependencies change
    checkAndMaximize();
    
    // Also listen for window resize to handle desktop/mobile transitions
    window.addEventListener('resize', checkAndMaximize);
    return () => window.removeEventListener('resize', checkAndMaximize);
  }, [chatMessages.length, typingBuffer, isThinking, isChatMaximized]);

  // Log isChatMaximized changes
  useEffect(() => {
    console.log('🔄 [LANDING] isChatMaximized changed:', {
      isChatMaximized,
      chatMessagesLength: chatMessages.length,
      timestamp: Date.now()
    });
    
    // Measure footer position when isChatMaximized changes
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const footer = document.querySelector('[data-footer="homepage"]');
        if (footer) {
          const rect = footer.getBoundingClientRect();
          console.log('📍 [LANDING] Footer position on isChatMaximized change:', {
            isChatMaximized,
            top: rect.top,
            bottom: rect.bottom,
            height: rect.height,
            y: rect.y,
            computedStyle: window.getComputedStyle(footer).position
          });
        }
        
        // Also measure ChatContainer
        const chatContainer = document.querySelector('[data-chat-container]');
        if (chatContainer) {
          const containerRect = chatContainer.getBoundingClientRect();
          console.log('📦 [LANDING] ChatContainer position on isChatMaximized change:', {
            isChatMaximized,
            top: containerRect.top,
            bottom: containerRect.bottom,
            height: containerRect.height,
            y: containerRect.y
          });
        }
      });
    });
  }, [isChatMaximized, chatMessages.length]);
  
  // Fetch pending shows when confirm shows form message is detected in chat
  useEffect(() => {
    console.log('🔄 [PENDING SHOWS] useEffect running, chatMessages length:', chatMessages.length);
    
    // Check if there's a message with the confirm shows marker
    const hasConfirmShowsMessage = chatMessages.some(
      (m) => m.role === 'assistant' && m.text && m.text.includes('[CONFIRM_SHOWS:form]')
    );
    
    console.log('🔍 [PENDING SHOWS] hasConfirmShowsMessage:', hasConfirmShowsMessage);
    console.log('🔍 [PENDING SHOWS] pendingShowsData.length:', pendingShowsData.length);
    
    // If the message exists and we haven't fetched data yet, fetch it
    if (hasConfirmShowsMessage && pendingShowsData.length === 0) {
      console.log('📋 [PENDING SHOWS] Detected confirm shows message, fetching data...');
      
      const fetchPendingShows = async () => {
        try {
          const token = JSON.parse(localStorage.getItem('dizeeToken'));
          console.log('📋 [PENDING SHOWS] Token:', token);
          
          const response = await getDataAPI('user/getBookings', token);
          
          console.log('📋 [PENDING SHOWS] Full API Response:', response);
          console.log('📋 [PENDING SHOWS] Response.data:', response?.data);
          console.log('📋 [PENDING SHOWS] Response.data.status:', response?.data?.status);
          console.log('📋 [PENDING SHOWS] Response.data.data type:', typeof response?.data?.data);
          console.log('📋 [PENDING SHOWS] Response.data.data length:', Array.isArray(response?.data?.data) ? response?.data?.data.length : 'not an array');
          console.log('📋 [PENDING SHOWS] Response.data.data keys:', response?.data?.data ? Object.keys(response.data.data) : 'null');
          console.log('📋 [PENDING SHOWS] Response.data.data value:', response?.data?.data);
          
          // Try different response structures
          let allBookings = null;
          
          // Check if response.data.data is an object with a bookings array inside
          if (response?.data?.data && typeof response.data.data === 'object') {
            // Check if it has a bookings property that's an array
            if (Array.isArray(response.data.data.bookings)) {
              allBookings = response.data.data.bookings;
              console.log('📋 [PENDING SHOWS] Using response.data.data.bookings');
            } 
            // Check if it has a data property that's an array
            else if (Array.isArray(response.data.data.data)) {
              allBookings = response.data.data.data;
              console.log('📋 [PENDING SHOWS] Using response.data.data.data');
            }
            // Check if data itself is structured with nested properties
            else if (response.data.data.allBookings && Array.isArray(response.data.data.allBookings)) {
              allBookings = response.data.data.allBookings;
              console.log('📋 [PENDING SHOWS] Using response.data.data.allBookings');
            }
            // If it's a single object, wrap it in an array
            else if (response.data.data._id) {
              allBookings = [response.data.data];
              console.log('📋 [PENDING SHOWS] Single booking object, wrapped in array');
            }
            else {
              console.warn('📋 [PENDING SHOWS] response.data.data is an object but no bookings array found');
              console.warn('📋 [PENDING SHOWS] Object structure:', JSON.stringify(response.data.data, null, 2));
            }
          }
          // Original checks
          else if (Array.isArray(response?.data?.data)) {
            allBookings = response.data.data;
            console.log('📋 [PENDING SHOWS] Using response.data.data (array)');
          } else if (Array.isArray(response?.data)) {
            allBookings = response.data;
            console.log('📋 [PENDING SHOWS] Using response.data directly');
          } else if (Array.isArray(response)) {
            allBookings = response;
            console.log('📋 [PENDING SHOWS] Using response directly (array)');
          }
          
          if (allBookings && Array.isArray(allBookings)) {
            console.log('📋 [PENDING SHOWS] All bookings count:', allBookings.length);
            console.log('📋 [PENDING SHOWS] Sample booking:', allBookings[0]);
            
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            
            // Filter for pending shows (no status or status 'pending')
            const pending = allBookings.filter((booking) => {
              if (booking.status && booking.status !== 'pending') return false;
              if (booking.perf_date) {
                const perfDate = new Date(booking.perf_date);
                perfDate.setHours(0, 0, 0, 0);
                return perfDate >= today;
              }
              return true;
            }).map((booking) => ({
              id: booking._id,
              date: booking.perf_date || 'TBD',
              venue: booking.venue || 'Venue TBD',
              location: booking.city_state || 'Location TBD',
              artist: booking.artist || 'Artist TBD'
            }));
            
            console.log('📋 [PENDING SHOWS] Filtered pending shows count:', pending.length);
            console.log('📋 [PENDING SHOWS] Filtered pending shows:', pending);
            setPendingShowsData(pending);
          } else {
            console.warn('📋 [PENDING SHOWS] Could not find bookings array in response');
            console.warn('📋 [PENDING SHOWS] Response keys:', response ? Object.keys(response) : 'response is null');
          }
        } catch (error) {
          console.error('❌ [PENDING SHOWS] Error fetching pending shows:', error);
          console.error('❌ [PENDING SHOWS] Error details:', {
            message: error.message,
            response: error.response,
            stack: error.stack
          });
        }
      };
      
      fetchPendingShows();
    }
  }, [chatMessages, pendingShowsData.length]);
  
  // Track ConversationArea mounting/unmounting (this is likely causing the jump)
  useEffect(() => {
    const hasConversationArea = chatMessages.length > 0 || typingBuffer || isThinking;
    
    if (hasConversationArea) {
      console.log('✅ [LANDING] ConversationArea is MOUNTED');
    } else {
      console.log('❌ [LANDING] ConversationArea is UNMOUNTING - this might cause footer jump!');
      
      // Measure footer position when ConversationArea unmounts
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const footer = document.querySelector('[data-footer="homepage"]');
          if (footer) {
            const rect = footer.getBoundingClientRect();
            console.log('📍 [LANDING] Footer position when ConversationArea unmounts:', {
              top: rect.top,
              bottom: rect.bottom,
              height: rect.height,
              y: rect.y
            });
          }
        });
      });
    }
  }, [chatMessages.length, typingBuffer, isThinking]);
  
  // Detect manual scrolling
  useEffect(() => {
    const scrollContainer = conversationRef.current;
    if (!scrollContainer) return;
    
    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 50; // Within 50px of bottom
      
      // If user scrolls up (not near bottom), set flag
      if (!isNearBottom) {
        setUserHasScrolledUp(true);
      } else {
        // If user scrolls back to bottom, resume auto-scroll
        setUserHasScrolledUp(false);
      }
    };
    
    scrollContainer.addEventListener('scroll', handleScroll);
    return () => scrollContainer.removeEventListener('scroll', handleScroll);
  }, []);
  
  // Lock body scroll to prevent any page scrolling
  useEffect(() => {
    // Get viewport height for mobile
    const vh = window.innerHeight;
    
    document.body.style.overflow = 'hidden';
    document.body.style.height = '100vh';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.style.top = '0';
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.touchAction = 'none';
    document.documentElement.style.overflow = 'hidden';
    document.documentElement.style.height = '100vh';
    document.documentElement.style.touchAction = 'none';
    
    return () => {
      document.body.style.overflow = '';
      document.body.style.height = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.touchAction = '';
      document.documentElement.style.overflow = '';
      document.documentElement.style.height = '';
      document.documentElement.style.touchAction = '';
    };
  }, []);
  
  // Analytics tracking
  const { trackEvent, trackNavigation } = useAnalyticsEvent();
  
  // Get user data from Redux store
  const { userArticle } = useSelector(state => state.user);

  // Function to load artist profile when team member is acting on their behalf
  const loadArtistProfile = async (selectedArtist) => {
    try {
      console.log('🎭 Setting up delegated access for team member to act on behalf of:', selectedArtist.name);
      console.log('🎭 Team member will retain their own identity but access artist data');
      
      // DON'T override userArticle - keep the team member as the logged-in user
      // The selectedArtist in localStorage + API interceptor will handle the data delegation
      
      console.log('✅ Delegated access context established - team member can now access artist data while remaining themselves');
      
    } catch (error) {
      console.error('❌ Error setting up delegated access:', error);
    }
  };

  // Define all apps
  const isDemo = isCurrentUserDemo();
  const allApps = [
    {
      id: 'data',
      name: 'Data',
      icon: '🔗',
      color: '#FF2300',
      gradient: 'linear-gradient(135deg, #FF2300 0%, #E01F00 100%)',
      onClick: () => {
        trackEvent('app_access', { appName: 'data', from: 'landing_page' });
        navigate('/data');
      }
    },
    {
      id: 'catalog',
      name: 'Catalog',
      icon: '📋', 
      color: '#17A2B8',
      gradient: 'linear-gradient(135deg, #00BCD4 0%, #0097A7 100%)',
      onClick: () => {
        trackEvent('app_access', { appName: 'catalog', from: 'landing_page' });
        navigate('/catalog');
      }
    },
    {
      id: 'bookings',
      name: 'Bookings',
      icon: '📅',
      color: '#FFFF00',
      gradient: 'linear-gradient(135deg, #FFE66D 0%, #FFEB3B 100%)',
      onClick: () => {
        trackEvent('app_access', { appName: 'bookings', from: 'landing_page' });
        navigate('/bookings');
      }
    },
    {
      id: 'tourmngr',
      name: 'TourMngr',
      icon: '🚐',
      color: '#FF9500',
      gradient: 'linear-gradient(135deg, #FF9500 0%, #FF5E3A 100%)',
      onClick: () => {
        trackEvent('app_access', { appName: 'tourmngr', from: 'landing_page' });
        navigate('/tour-manager/view-all');
      }
    },
    {
      id: 'email',
      name: 'Email',
      icon: '📧',
      color: '#CDD4DB',
      gradient: 'linear-gradient(135deg, #CDD4DB 0%, #A8B5C0 100%)',
      onClick: () => {
        trackEvent('app_access', { appName: 'email', from: 'landing_page' });
        navigate('/email');
      }
    },
    {
      id: 'tasks',
      name: 'Tasks',
      icon: '✅',
      color: '#00CED1',
      gradient: 'linear-gradient(135deg, #00CED1 0%, #20B2AA 100%)',
      onClick: () => {
        trackEvent('app_access', { appName: 'tasks', from: 'landing_page' });
        navigate('/tasks');
      }
    },
    {
      id: 'workflows',
      name: 'Workflows',
      icon: '⚡',
      color: '#AAFF00',
      gradient: 'linear-gradient(135deg, #AAFF00 0%, #88CC00 100%)',
      onClick: () => {
        trackEvent('app_access', { appName: 'workflows', from: 'landing_page' });
        navigate('/flows');
      }
    },
    {
      id: 'team-management',
      name: 'Teams',
      icon: '👨‍👩‍👧‍👦',
      color: '#8B5CF6',
      gradient: 'linear-gradient(135deg, #A78BFA 0%, #8B5CF6 100%)',
      onClick: () => {
        trackEvent('app_access', { appName: 'team-management', from: 'landing_page' });
        navigate('/team-management');
      }
    },
    {
      id: 'notes',
      name: 'Notes',
      icon: '📝',
      color: '#F5F5F5',
      gradient: 'linear-gradient(135deg, #F5F5F5 0%, #E0E0E0 100%)',
      onClick: () => {
        trackEvent('app_access', { appName: 'notes', from: 'landing_page' });
        navigate('/notes');
      }
    },
    {
      id: 'contacts',
      name: 'Contacts',
      icon: '👥',
      color: '#B08A4C',
      gradient: 'linear-gradient(135deg, #B08A4C 0%, #8B6F47 100%)',
      onClick: () => {
        trackEvent('app_access', { appName: 'contacts', from: 'landing_page' });
        navigate('/contacts');
      }
    },
    {
      id: 'analytics',
      name: 'Analytics',
      icon: '📊',
      color: '#34C759',
      gradient: 'linear-gradient(135deg, #34C759 0%, #30D158 100%)',
      onClick: () => {
        trackEvent('app_access', { appName: 'analytics', from: 'landing_page' });
        navigate('/analytics');
      }
    },
    {
      id: 'knowledge',
      name: 'Knowledge',
      icon: '📚',
      color: '#FF1493',
      gradient: 'linear-gradient(135deg, #FF1493 0%, #E91E63 100%)',
      onClick: () => {
        trackEvent('app_access', { appName: 'knowledge', from: 'landing_page' });
        navigate('/knowledge-base');
      }
    },
    /* Temporarily hidden - not ready for MVP
    {
      id: 'merch',
      name: 'Merch',
      color: '#9932CC', 
      onClick: () => {
        trackEvent('app_access', { appName: 'merch', from: 'landing_page' });
        navigate('/merch');
      }
    },
    {
      id: 'charting',
      name: 'Charting',
      color: '#FF9500',
      onClick: () => {
        trackEvent('app_access', { appName: 'charting', from: 'landing_page' });
        navigate('/charting');
      }
    },
    {
      id: 'playlisting',
      name: 'Playlisting',
      color: '#5856D6',
      onClick: () => {
        trackEvent('app_access', { appName: 'playlisting', from: 'landing_page' });
        navigate('/playlisting');
      }
    },
    {
      id: 'social-listening',
      name: 'Social Listening',
      color: '#007AFF',
      onClick: () => {
        trackEvent('app_access', { appName: 'social-listening', from: 'landing_page' });
        navigate('/social-listening');
      }
    },
    */
    /* Spotify Revenue app temporarily hidden - uses mock data, not ready for production
    {
      id: 'spotify-revenue',
      name: 'Spotify Revenue',
      color: '#1DB954',
      onClick: () => {
        trackEvent('app_access', { appName: 'spotify-revenue', from: 'landing_page' });
        navigate('/spotify-revenue');
      }
    },
    */
  ];

  // Filter apps for demo users
  const apps = isDemo 
    ? allApps.filter(app => {
        // Hide these apps for demo users
        const hiddenApps = ['workflows', 'bookings', 'catalog', 'tourmngr', 'contacts', 'team-management', 'analytics'];
        return !hiddenApps.includes(app.id);
      })
    : allApps;

  // Apps per page for horizontal scrolling
  const appsPerPage = 4;
  const totalPages = Math.ceil(apps.length / appsPerPage);

  // Load user data when component mounts
  useEffect(() => {
    const token = localStorage.getItem('dizeeToken');
    if (token) {
      dispatch(clearAlerts());
      
      // Check current route - if we're on team-member routes, don't load user data here
      const currentPath = window.location.pathname;
      const isTeamMemberRoute = currentPath === '/team-member-dashboard' || 
                              currentPath === '/manager-dashboard' ||
                              currentPath === '/team-member/settings';
      
      // Don't load user data on team member routes - let the team dashboard handle it
      if (isTeamMemberRoute) {
        console.log('👤 On team member route - skipping user data load in Landing page');
        return;
      }
      
      // Check if team member is acting on behalf of an artist
      const selectedArtistData = localStorage.getItem('selectedArtist');
      if (selectedArtistData) {
        try {
          const selectedArtist = JSON.parse(selectedArtistData);
          console.log('🎭 Team member acting as artist:', selectedArtist);
          // Load the selected artist's profile data and set up delegated access
          loadArtistProfile(selectedArtist);
          // IMPORTANT: Still call the API to load user data - our backend interceptor will handle delegation
          console.log('🎭 Loading artist profile data via delegated access...');
          dispatch(get_user_articles_thunk({ token }));
        } catch (error) {
          console.error('Error parsing selectedArtist:', error);
          // Fallback to loading team member's own data
          dispatch(get_user_articles_thunk({ token }));
        }
      } else {
        // No selected artist, load the user's own data (artist or personal user)
        console.log('👤 Loading user\'s own profile');
      dispatch(get_user_articles_thunk({ token }));
      }
    }

    // Check if this is a new signup (not existing login) to show tutorial
    const isNewSignup = localStorage.getItem('isNewSignup');
    const hasSeenWalkthrough = localStorage.getItem('hasSeenWalkthrough');
    
    // Skip walkthrough - go straight to tutorial for NEW signups
    if (isNewSignup && !hasSeenWalkthrough) {
      // Mark walkthrough as seen (even though we're skipping it)
      localStorage.setItem('hasSeenWalkthrough', 'true');
      // Show tutorial directly
      setShowTutorial(true);
      // Clear the new signup flag since we're now showing the onboarding
      localStorage.removeItem('isNewSignup');
    }
  }, [dispatch]);

  // Note: Auto-population should only run during onboarding, not on every landing page visit

  // Handle scroll to update pagination
  useEffect(() => {
    const handleScroll = () => {
      if (appsScrollRef.current) {
        const container = appsScrollRef.current;
        const scrollLeft = container.scrollLeft;
        const appWidth = 90; // 70px width + 20px gap
        const appsVisible = Math.floor(container.clientWidth / appWidth);
        const scrolledApps = Math.round(scrollLeft / appWidth);
        const page = Math.floor(scrolledApps / appsVisible);
        setCurrentPage(Math.min(page, totalPages - 1));
      }
    };

    const container = appsScrollRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [totalPages]);

  // Listen for chat tab switch requests from sidebar
  useEffect(() => {
    const handleSwitchToChatTab = (event) => {
      console.log('📍 Received switchToChatTab event:', event.detail);
      setActiveTab('chat');
      localStorage.setItem('activeHomeTab', 'chat');
      
      if (event.detail?.maximize) {
        console.log('🔼 Maximizing chat view');
        setIsChatMaximized(true);
      }
    };

    window.addEventListener('switchToChatTab', handleSwitchToChatTab);
    return () => window.removeEventListener('switchToChatTab', handleSwitchToChatTab);
  }, []);

  // Detect when ticket counts form should be shown
  useEffect(() => {
    const lastMessage = chatMessages[chatMessages.length - 1];
    if (lastMessage && lastMessage.role === 'assistant' && lastMessage.text && lastMessage.text.startsWith('[ADD_TICKET_COUNTS:form]')) {
      setShowTicketCountsForm(true);
      // Fetch confirmed shows if not already loaded
      if (confirmedShows.length === 0) {
        fetchConfirmedShows();
      }
    }
  }, [chatMessages]);

  // Clean up section-specific and navigation messages when returning to landing page
  // Use a ref to track previous pathname to detect when we're returning (not just on landing page)
  const prevPathnameRef = useRef(location.pathname);
  useEffect(() => {
    // Only filter if we just returned to landing page (pathname changed from non-/ to /)
    const justReturnedToLanding = location.pathname === '/' && prevPathnameRef.current !== '/';
    const wasOnLanding = prevPathnameRef.current === '/';
    prevPathnameRef.current = location.pathname;
    
    // Only clean up when returning TO landing page, not when already on it or navigating away
    if (justReturnedToLanding && chatMessages.length > 0) {
      const hasUnwantedMessages = chatMessages.some(m => 
        m.showCurrentSection || 
        m.isInSection || 
        m.isNavigationMessage
      );
      
      if (hasUnwantedMessages) {
        console.log('🧹 Removing section-specific and navigation messages when returning to landing page');
        const filteredMessages = chatMessages.filter(m => 
          !m.showCurrentSection && 
          !m.isInSection &&
          !m.isNavigationMessage
        );
        // Only update if we actually filtered something out
        if (filteredMessages.length !== chatMessages.length) {
          setChatMessages(filteredMessages);
        }
      }
    }
  }, [location.pathname]);

  // Save messages whenever they change (for tour persistence)
  useEffect(() => {
    if (isTourActive() && chatMessages.length > 0) {
      // When on landing page, don't save section-specific messages
      // Navigation messages ARE saved (they're created on landing page before navigation)
      // They'll be filtered out when restoring on landing page later
      if (location.pathname === '/') {
        const filteredMessages = chatMessages.filter(m => 
          !m.showCurrentSection && 
          !m.isInSection
          // Note: Navigation messages ARE included here - they show during navigation
          // but get filtered out when returning to landing page (in restore logic)
        );
        if (filteredMessages.length > 0) {
          saveTourMessages(filteredMessages);
        }
      } else {
        // On section pages, save all messages (including navigation messages)
        saveTourMessages(chatMessages);
      }
    }
  }, [chatMessages, location.pathname]);

  // Handle browser back/forward navigation to restore tour state
  useEffect(() => {
    const handlePopState = () => {
      // Small delay to ensure location has updated
      setTimeout(() => {
        if (location.pathname === '/' && isTourActive()) {
          // Restore checklist state first - always load from localStorage
          isLoadingChecklistRef.current = true;
          const saved = localStorage.getItem('setupChecklistItems');
          if (saved) {
            try {
              const parsed = JSON.parse(saved);
              console.log('🔄 [BROWSER BACK] Restoring checklist from localStorage:', parsed);
              // Mark as loaded BEFORE updating state
              checklistLoadedRef.current = true;
              setChecklistItems(parsed);
            } catch (e) {
              console.error('❌ Error loading checklist on back navigation:', e);
            }
          } else {
            console.log('⚠️ No checklist items found on browser back');
          }
          setTimeout(() => {
            isLoadingChecklistRef.current = false;
          }, 100);
          
          // Restore tour messages, but prioritize checklist if not completed
          const checklistCompleted = isChecklistCompleted();
          if (!checklistCompleted) {
            // Show checklist message, not saved tour messages
            const checklistMessage = {
              role: 'assistant',
              text: `Welcome to DIZEE! Let's get your account set up.

I'll guide you through a quick checklist to make sure everything is ready to go.`,
              isTourMessage: true,
              isChecklistMessage: true
            };
            setChatMessages([checklistMessage]);
          } else {
            // Checklist completed, restore saved tour messages
            const savedMessages = loadTourMessages();
            if (savedMessages && savedMessages.length > 0) {
              // Filter out checklist messages, section-specific messages, and navigation messages
              // These shouldn't be on landing page
              const filteredMessages = savedMessages.filter(m => 
                !m.isChecklistMessage && 
                !m.showCurrentSection && 
                !m.isInSection &&
                !m.isNavigationMessage
              );
              if (filteredMessages.length > 0) {
                setChatMessages(filteredMessages);
              } else {
                // No valid messages after filtering, show welcome
                const welcome = getPostChecklistWelcomeMessage();
                setChatMessages([welcome]);
              }
            } else {
              // No saved messages, show welcome
              const welcome = getPostChecklistWelcomeMessage();
              setChatMessages([welcome]);
            }
          }
          
          setIsChatMaximized(true);
          setActiveTab('chat');
        }
      }, 100);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [location.pathname]);

  // Initialize checklist state if checklist message is present
  useEffect(() => {
    const hasChecklistMessage = chatMessages.some(m => m.isChecklistMessage);
    if (hasChecklistMessage) {
      // Auto-maximize chat to show checklist
      setIsChatMaximized(true);
      
      // Set loading flag to prevent save effect from running
      isLoadingChecklistRef.current = true;
      
      // Always load saved checklist items when checklist message is shown
      const saved = localStorage.getItem('setupChecklistItems');
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          console.log('🔄 [CHECKLIST MESSAGE] Loading checklist from localStorage:', parsed);
          // Mark as loaded BEFORE updating state to prevent save effect from overwriting
          checklistLoadedRef.current = true;
          // Force update from localStorage
          setChecklistItems(parsed);
        } catch (e) {
          console.error('❌ Error loading checklist items:', e);
        }
      } else {
        // No saved data, mark as loaded so we can save future changes
        checklistLoadedRef.current = true;
      }
      
      // Clear loading flag after a brief delay
      setTimeout(() => {
        isLoadingChecklistRef.current = false;
      }, 100);
    }
  }, [chatMessages]);

  const handleWalkthroughClose = () => {
    setShowWalkthrough(false);
    console.log("Walkthrough closed, showing tutorial next");

    // Show tutorial after a brief delay
    setTimeout(() => {
      setShowTutorial(true);
      console.log("Tutorial should be visible now");
    }, 50);
  };

  const handleTutorialClose = () => {
    setShowTutorial(false);
    localStorage.setItem('hasSeenTutorial', 'true');
    
    // Trigger account setup check immediately
    window.dispatchEvent(new CustomEvent('tutorialCompleted'));
  };

  const handleMoreApps = () => {
    navigate('/apps/all');
  };

  const toggleDashboard = () => {
    const newCollapsedState = !isDashboardCollapsed;
    setIsDashboardCollapsed(newCollapsedState);
    // Save collapse state to localStorage
    localStorage.setItem('dashboardCollapsed', JSON.stringify(newCollapsedState));
  };

  // Handle tab changes
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    localStorage.setItem('activeHomeTab', tab);
    trackEvent('home_tab_change', { tab });
  };

  // Handle category filter changes
  const handleCategoryChange = (category) => {
    setActiveNavItem(category);
  };

  // Handle apps view toggle (expanded/simplified)
  const handleAppsViewToggle = () => {
    const newExpandedState = !isAppsExpanded;
    setIsAppsExpanded(newExpandedState);
    localStorage.setItem('appsViewExpanded', JSON.stringify(newExpandedState));
    trackEvent('apps_view_toggle', { expanded: newExpandedState });
  };

  // ===== CHECKLIST HANDLERS =====
  const handleChecklistToggle = (itemId) => {
    setChecklistItems(prev => {
      const updated = prev.includes(itemId)
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId];
      
      // Save immediately to localStorage
      localStorage.setItem('setupChecklistItems', JSON.stringify(updated));
      console.log('💾 Checklist item toggled, saved to localStorage:', updated);
      checklistLoadedRef.current = true;
      
      return updated;
    });
  };

  const handleChecklistNavigate = (route) => {
    // Save current state before navigating
    saveTourMessages(chatMessages);
    
    // Save current checklist items to localStorage before navigating
    // Use the current state value directly
    const currentItems = checklistItems;
    localStorage.setItem('setupChecklistItems', JSON.stringify(currentItems));
    console.log('💾 [NAVIGATE] Saving checklist state before navigation:', currentItems);
    checklistLoadedRef.current = true;
    
    // Ensure tour stays active
    setTourActive(true);
    // Navigate to the route
    navigate(route);
  };

  const handleChecklistComplete = () => {
    // Mark checklist as completed
    localStorage.setItem('setupChecklistCompleted', Date.now().toString());
    
    // Transition to post-checklist welcome message
    const welcome = getPostChecklistWelcomeMessage();
    setChatMessages(prev => {
      // Replace checklist message with welcome message
      const updated = prev.filter(msg => !msg.isChecklistMessage);
      return [...updated, welcome];
    });
    
    // Save messages
    setChatMessages(current => {
      saveTourMessages(current);
      return current;
    });
  };

  // Calculate checklist progress
  const checklistProgress = checklistItems.length;
  const checklistTotal = CHECKLIST_ITEMS_CONFIG.length;
  const checklistPercentage = Math.round((checklistProgress / checklistTotal) * 100);

  // ===== TOUR HANDLERS =====
  const getNextSection = (currentSection) => {
    const sections = Object.keys(TOUR_SECTIONS);
    const currentIndex = sections.indexOf(currentSection);
    return sections[currentIndex + 1] || null;
  };

  const handleTourOptionSelect = (option) => {
    console.log('📍 Tour option selected:', option);
    
    if (option === 'continue') {
      handleTourContinue();
      return;
    }
    
    if (option === 'menu') {
      handleTourMenu();
      return;
    }
    
    if (option === 'done') {
      handleTourDone();
      return;
    }
    
    // Handle section selection (bookings, catalog, etc)
    handleTourSectionSelect(option);
  };
  
  const handleTourSectionSelect = async (sectionName) => {
    setCurrentTourSectionState(sectionName);
    setCurrentTourSection(sectionName);
    
    const tourSection = TOUR_CONTENT[sectionName];
    if (!tourSection) return;
    
    const sectionInfo = TOUR_SECTIONS[sectionName];
    const startTime = Date.now();
    const navigationId = `nav-${Date.now()}`; // Unique ID for this navigation message
    
    // Show the interactive "taking you there" message (without user message)
    const takingYouThereMessage = {
      role: 'assistant',
      text: tourSection.intro.navigationText || tourSection.intro.text.split('\n\n')[0],
      isTourMessage: true,
      isNavigationMessage: true, // Mark as navigation to style differently
      navigationId: navigationId // Add ID to find and update later
    };
    
    // Append navigation message to existing chat (keep conversation flow)
    // Save immediately including navigation message BEFORE navigation
    setChatMessages(prev => {
      const updated = [...prev, takingYouThereMessage];
      // Save including navigation message - it will show on app page
      // but be filtered out when returning to landing page
      saveTourMessages(updated);
      return updated;
    });
    
    // Check user data if needed for this section
    let hasData = false;
    if (tourSection.intro.checkUserData) {
      const data = await checkUserData();
      hasData = (sectionName === 'catalog' && data.catalogHasData) || 
                (sectionName === 'bookings' && data.bookingsHasData);
    }
    
    // Then navigate after showing the message
    if (tourSection.intro.navigate) {
      setIsNavigatingTour(true);
      setTimeout(async () => {
        navigate(tourSection.intro.navigate);
        setIsNavigatingTour(false);
        
        // Update the navigation message to show completion
        const elapsedTime = Math.round((Date.now() - startTime) / 1000);
        setChatMessages(prev => prev.map(msg => 
          msg.navigationId === navigationId 
            ? {
                ...msg,
                text: msg.text.replace('Navigating to', 'Navigated to') + ` (${elapsedTime}s)`
              }
            : msg
        ));
        
        // Show onArrival message based on data state
        setTimeout(() => {
          const onArrival = tourSection.onArrival;
          if (onArrival) {
            // Determine which text to show
            let arrivalText = onArrival.text;
            if (onArrival.textIfHasData && hasData) {
              arrivalText = onArrival.textIfHasData;
            } else if (onArrival.textIfEmpty && !hasData) {
              arrivalText = onArrival.textIfEmpty;
            }
            
            // Filter buttons based on data state
            let filteredButtons = onArrival.buttons;
            if (filteredButtons) {
              filteredButtons = filteredButtons.filter(btn => {
                if (btn.showIfHasData && !hasData) return false;
                if (btn.showIfEmpty && hasData) return false;
                return true;
              });
            }
            
            const arrivalMessage = {
              role: 'assistant',
              text: arrivalText,
              isTourMessage: true,
              buttons: filteredButtons || undefined,
              showOptions: onArrival.showOptions,
              options: onArrival.options
            };
            
            setChatMessages(prev => [...prev, arrivalMessage]);
          }
        }, 500);
        
        // Save the updated messages
        setChatMessages(current => {
          saveTourMessages(current);
          return current;
        });
      }, tourSection.intro.delay || 1500);
    }
  };
  
  const handleTourContinue = () => {
    setChatMessages(prev => [...prev, {
      role: 'user',
      text: 'Continue to next section',
      isTourMessage: true
    }]);
    
    setTimeout(() => {
      const nextSection = getNextSection(currentTourSectionState);
      
      if (nextSection) {
        setChatMessages(prev => [...prev, {
          role: 'assistant',
          text: QUICK_RESPONSES.continue,
          isTourMessage: true
        }]);
        
        setTimeout(() => {
          handleTourSectionSelect(nextSection);
        }, 800);
      } else {
        // Tour complete!
        setChatMessages(prev => [...prev, {
          role: 'assistant',
          text: QUICK_RESPONSES.done,
          showOptions: true,
          options: ['done'],
          isTourMessage: true
        }]);
        completeTour();
      }
    }, 300);
  };
  
  const handleTourMenu = () => {
    setChatMessages(prev => [...prev, {
      role: 'user',
      text: 'Reload menu',
      isTourMessage: true
    }]);
    
    setTimeout(() => {
      setChatMessages(prev => [...prev, {
        role: 'assistant',
        text: QUICK_RESPONSES.menu,
        isTourMessage: true
      }]);
      
      setTimeout(() => {
        setChatMessages(prev => [...prev, {
          role: 'assistant',
          text: "Ready to dive in? I can teach you about:",
          showOptions: true,
          options: Object.keys(TOUR_SECTIONS),
          showQuestions: true,
          isTourMessage: true
        }]);
      }, 500);
    }, 300);
  };
  
  const handleTourDone = () => {
    setChatMessages(prev => [...prev, {
      role: 'user',
      text: "I'm ready to start!",
      isTourMessage: true
    }]);
    
    setTimeout(() => {
      setChatMessages(prev => [...prev, {
        role: 'assistant',
        text: "🎉 Awesome! You're all set. Feel free to explore on your own or ask me anything. I'm here whenever you need help!",
        isTourMessage: true
      }]);
      completeTour();
    }, 300);
  };
  
  const handleTourQuestionSelect = (question) => {
    console.log('❓ Tour question asked:', question);
    
    // Add user's question
    setChatMessages(prev => [...prev, {
      role: 'user',
      text: question,
      isTourMessage: true
    }]);
    
    // Get answer
    const answer = QUESTION_ANSWERS[question];
    if (answer) {
      setTimeout(() => {
        setChatMessages(prev => [...prev, {
          role: 'assistant',
          text: answer.text,
          showOptions: answer.showOptions,
          options: answer.options,
          isTourMessage: true
        }]);
      }, 400);
    }
  };
  
  // Handle onboarding button actions
  const handleOnboardingButtonAction = async (button) => {
    console.log('🎯 Button clicked:', button);
    
    // Add user message showing they clicked the button
    setChatMessages(prev => [...prev, {
      role: 'user',
      text: button.text,
      isTourMessage: true
    }]);
    
    // Handle different action types
    switch (button.action) {
      case 'navigate':
        // Navigate to the target route
        setTimeout(() => {
          navigate(button.target);
        }, 300);
        break;
        
      case 'navigate_tour':
        // Navigate to another tour section (e.g., from catalog to bookings)
        setTimeout(() => {
          handleTourSectionSelect(button.target);
        }, 300);
        break;
        
      case 'import_spotify':
        // TODO: Trigger Spotify import flow
        // For now, just show importing message
        setChatMessages(prev => [...prev, {
          role: 'assistant',
          text: 'Starting Spotify import... This will take a moment.',
          isTourMessage: true
        }]);
        
        // Simulate import completing after a delay
        setTimeout(async () => {
          const newData = await checkUserData();
          setUserData(newData);
          
          setChatMessages(prev => [...prev, {
            role: 'assistant',
            text: 'Import complete! Your catalog has been loaded.',
            isTourMessage: true
          }]);
        }, 3000);
        break;
        
      case 'complete_onboarding':
        // Complete the tour
        setChatMessages(prev => [...prev, {
          role: 'assistant',
          text: '🎉 Congratulations! You\'ve completed the tour. You\'re all set to start using DIZEE!',
          isTourMessage: true
        }]);
        completeTour();
        break;
        
      default:
        console.warn('Unknown button action:', button.action);
    }
  };

  // Handle maximize/minimize chat
  const handleMaximizeChat = (forceValue) => {
    // Use requestAnimationFrame for smooth transition
    requestAnimationFrame(() => {
      const isDesktop = typeof window !== 'undefined' && window.innerWidth >= 768;
      
      if (typeof forceValue === 'boolean') {
        // If explicit value provided, set it directly
        // On desktop, prevent minimizing if there are messages
        if (isDesktop && !forceValue && (chatMessages.length > 0 || typingBuffer || isThinking)) {
          return; // Don't allow minimizing on desktop when chat is active
        }
        setIsChatMaximized(forceValue);
      } else {
        // Otherwise toggle
        // On desktop, prevent toggling to minimized if there are messages
        if (isDesktop && (chatMessages.length > 0 || typingBuffer || isThinking)) {
          setIsChatMaximized(true); // Always keep maximized on desktop
        } else {
    setIsChatMaximized(prev => !prev);
        }
      }
    });
  };
  
  const handleMinimizeChat = () => {
    // Use requestAnimationFrame for smooth transition
    requestAnimationFrame(() => {
      const isDesktop = typeof window !== 'undefined' && window.innerWidth >= 768;
      // On desktop, prevent minimizing if there are messages
      if (isDesktop && (chatMessages.length > 0 || typingBuffer || isThinking)) {
        return; // Don't allow minimizing on desktop when chat is active
      }
    setIsChatMaximized(false);
    });
  };
  
  // Handle new chat - fade out first, then clear (reverse of how chat starts)
  const handleNewChat = () => {
    // Step 1: Start fade out animation
    setIsFadingOut(true);
    
    // Step 2: After fade completes, clear state (buttons will fade out automatically)
    // This matches the 0.3s transition duration in FooterBar
    setTimeout(() => {
    setChatMessages([]);
    setIsTyping(false);
    setTypingBuffer('');
    setIsThinking(false);
      setShowTicketCountsForm(false);
    setUserHasScrolledUp(false);
    setIsChatMaximized(false);
      
      // Step 3: Reset fade state after layout settles
      setTimeout(() => {
        setIsFadingOut(false);
      }, 100);
    }, 300); // Match the fade transition duration
  };

  // Handle message sent - maximize chat
  const handleMessageSent = () => {
    setIsChatMaximized(true);
    setUserHasScrolledUp(false); // Reset scroll flag when sending new message
  };

  // Scroll to specific page
  const scrollToPage = (page) => {
    if (appsScrollRef.current) {
      const container = appsScrollRef.current;
      const appWidth = 90; // 70px width + 20px gap
      const appsVisible = Math.floor(container.clientWidth / appWidth);
      const scrollLeft = page * (appsVisible * appWidth);
      container.scrollTo({ left: scrollLeft, behavior: 'smooth' });
    }
  };

  // Catalog form handlers
  const handleCatalogFormSubmit = async () => {
    // Validation
    if (!catalogFormData.songTitle.trim()) {
      alert('Please enter a song title');
      return;
    }
    
    const validPrimaryArtists = catalogFormData.primaryArtists.filter(a => a.trim());
    if (validPrimaryArtists.length === 0) {
      alert('Please enter at least one primary artist');
      return;
    }

    setIsSubmittingForm(true);

    // Prepare artist names and catalog item outside try block for error logging
    const artistName = validPrimaryArtists.join(', ');
    const validFeaturedArtists = catalogFormData.featuredArtists.filter(a => a.trim());
    const featuredArtistName = validFeaturedArtists.length > 0 ? validFeaturedArtists.join(', ') : undefined;
    
    // Create catalog item - saveCatalog expects an array in 'data' field
    // Field names match what mapEditCatalogToDatabase expects
    const catalogItem = {
      song_title: catalogFormData.songTitle.trim(),
      artist_name: artistName,
      featured_artists: featuredArtistName,
      status: catalogFormType === 'demo' ? 'demo' : catalogFormData.releaseStatus,
      release_date: catalogFormData.releaseDate || undefined,
      notes: catalogFormData.additionalInfo.trim() || undefined
    };

    try {
      const token = JSON.parse(localStorage.getItem('dizeeToken'));
      const response = await postDataAPI('user/saveCatalog', { data: [catalogItem] }, token);
      
      // Get the created catalog item ID from response
      const createdItemId = response?.data?.data?.[0]?._id;
      console.log('✅ Catalog item created:', createdItemId);

      // Build a natural message about what was added
      const itemType = catalogFormType === 'demo' ? 'demo' : 'upcoming release';
      let summaryMessage = `I just added a new ${itemType} to my catalog:\n`;
      summaryMessage += `• Song Title: ${catalogFormData.songTitle}\n`;
      summaryMessage += `• Artist: ${artistName}`;
      if (featuredArtistName) {
        summaryMessage += `\n• Featured Artists: ${featuredArtistName}`;
      }
      if (catalogFormData.releaseDate) {
        summaryMessage += `\n• Release Date: ${catalogFormData.releaseDate}`;
      }
      if (catalogFormData.additionalInfo) {
        summaryMessage += `\n• Notes: ${catalogFormData.additionalInfo}`;
      }

      // Clear the form immediately so it doesn't show in chat history
      setCatalogFormType(null);
      setCatalogFormData({
        songTitle: '',
        primaryArtists: [''],
        featuredArtists: [''],
        releaseStatus: 'upcoming',
        releaseDate: '',
        additionalInfo: ''
      });

      // Send the summary message to AI for natural response
      // This will be saved in conversation history automatically
      console.log('📤 Sending catalog confirmation to AI:', summaryMessage);
      window.dispatchEvent(new CustomEvent('sendAIMessage', {
        detail: {
          message: summaryMessage,
          catalogId: createdItemId
        }
      }));

    } catch (error) {
      console.error('Error submitting catalog form:', error);
      console.error('Error details:', error.response?.data || error.message);
      console.error('Catalog data sent:', catalogItem);
      
      const errorMessage = {
        role: 'assistant',
        text: `❌ Sorry, there was an error adding your ${catalogFormType === 'demo' ? 'demo' : 'release'}. ${error.response?.data?.msg || 'Please try again.'}`
      };
      setChatMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsSubmittingForm(false);
    }
  };

  // Booking form handlers
  const handleBookingFormSubmit = async () => {
    // Validation
    if (!bookingFormData.venue.trim()) {
      alert('Please enter a venue');
      return;
    }
    
    if (!bookingFormData.artist.trim()) {
      alert('Please enter an artist name');
      return;
    }
    
    if (!bookingFormData.perfDate) {
      alert('Please select a performance date');
      return;
    }
    
    if (!bookingFormData.city.trim() || !bookingFormData.state.trim()) {
      alert('Please enter both city and state');
      return;
    }

    setIsSubmittingBookingForm(true);

    // Create booking item
    const bookingItem = {
      venue: bookingFormData.venue.trim(),
      artist: bookingFormData.artist.trim(),
      perf_date: bookingFormData.perfDate,
      city: bookingFormData.city.trim(),
      state: bookingFormData.state.trim(),
      city_state: `${bookingFormData.city.trim()}, ${bookingFormData.state.trim()}`,
      notes: bookingFormData.additionalInfo.trim() || undefined
    };

    try {
      const token = JSON.parse(localStorage.getItem('dizeeToken'));
      const response = await postDataAPI('user/saveBookings', { data: [bookingItem] }, token);
      
      // Get the created booking item ID from response
      const createdItemId = response?.data?.data?.[0]?._id;
      console.log('✅ Booking item created:', createdItemId);

      // Build a natural message about what was added
      let summaryMessage = `I just added a new show:\n`;
      summaryMessage += `• Venue: ${bookingFormData.venue}\n`;
      summaryMessage += `• Artist: ${bookingFormData.artist}\n`;
      summaryMessage += `• Performance Date: ${bookingFormData.perfDate}\n`;
      summaryMessage += `• Location: ${bookingFormData.city}, ${bookingFormData.state}`;
      if (bookingFormData.additionalInfo) {
        summaryMessage += `\n• Notes: ${bookingFormData.additionalInfo}`;
      }

      // Clear the form immediately so it doesn't show in chat history
      setBookingFormType(null);
      setBookingFormData({
        venue: '',
        artist: '',
        perfDate: '',
        city: '',
        state: '',
        additionalInfo: ''
      });

      // Send the summary message to AI for natural response
      console.log('📤 Sending booking confirmation to AI:', summaryMessage);
      window.dispatchEvent(new CustomEvent('sendAIMessage', {
        detail: {
          message: summaryMessage,
          bookingId: createdItemId
        }
      }));

    } catch (error) {
      console.error('❌ Error submitting booking form:', error);
      console.error('📋 Error response:', error.response);
      console.error('📋 Error data:', error.response?.data);
      console.error('📋 Error message:', error.response?.data?.msg || error.message);
      console.error('📋 Booking data sent:', bookingItem);
      console.error('📋 Full booking item:', JSON.stringify(bookingItem, null, 2));
      
      const errorMessage = {
        role: 'assistant',
        text: `❌ Sorry, there was an error adding your show. ${error.response?.data?.msg || error.message || 'Please try again.'}`
      };
      setChatMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsSubmittingBookingForm(false);
    }
  };

  const handleBookingFormCancel = () => {
    setBookingFormData({
      venue: '',
      artist: '',
      perfDate: '',
      city: '',
      state: '',
      additionalInfo: ''
    });
    setBookingFormType(null);
  };

  const handleCatalogFormCancel = () => {
    setCatalogFormData({
      songTitle: '',
      primaryArtists: [''],
      featuredArtists: [''],
      releaseStatus: 'upcoming',
      releaseDate: '',
      additionalInfo: ''
    });
    setCatalogFormType(null);
    
    // Add cancellation message
    const cancelMessage = {
      role: 'assistant',
      text: 'No problem! Let me know if you need anything else.'
    };
    setChatMessages(prev => [...prev, cancelMessage]);
  };

  const addPrimaryArtist = () => {
    if (catalogFormData.primaryArtists.length < 6) {
      setCatalogFormData(prev => ({
        ...prev,
        primaryArtists: [...prev.primaryArtists, '']
      }));
    }
  };

  const removePrimaryArtist = (index) => {
    if (catalogFormData.primaryArtists.length > 1) {
      setCatalogFormData(prev => ({
        ...prev,
        primaryArtists: prev.primaryArtists.filter((_, i) => i !== index)
      }));
    }
  };

  const updatePrimaryArtist = (index, value) => {
    setCatalogFormData(prev => ({
      ...prev,
      primaryArtists: prev.primaryArtists.map((artist, i) => i === index ? value : artist)
    }));
  };

  const addFeaturedArtist = () => {
    if (catalogFormData.featuredArtists.length < 6) {
      setCatalogFormData(prev => ({
        ...prev,
        featuredArtists: [...prev.featuredArtists, '']
      }));
    }
  };

  const removeFeaturedArtist = (index) => {
    if (catalogFormData.featuredArtists.length > 1) {
      setCatalogFormData(prev => ({
        ...prev,
        featuredArtists: prev.featuredArtists.filter((_, i) => i !== index)
      }));
    }
  };

  const updateFeaturedArtist = (index, value) => {
    setCatalogFormData(prev => ({
      ...prev,
      featuredArtists: prev.featuredArtists.map((artist, i) => i === index ? value : artist)
    }));
  };

  // Fetch confirmed shows for ticket counts form (optimized endpoint)
  const fetchConfirmedShows = async () => {
    // Don't refetch if we already have the data
    if (confirmedShows.length > 0) {
      return;
    }

    setIsLoadingConfirmedShows(true);

    try {
      const token = localStorage.getItem('dizeeToken');
      console.log('🎫 Fetching confirmed shows...');
      const response = await getDataAPI('user/getConfirmedShows', token);

      console.log('📦 Full response:', response);
      console.log('📦 Response data:', response.data);
      console.log('📦 Response data.data:', response.data?.data);
      console.log('📦 Response data.data.data:', response.data?.data?.data);

      // Backend returns: { success: true, data: { data: [shows] } }
      const shows = response.data?.data?.data || [];
      
      if (shows && shows.length > 0) {
        console.log('✅ Confirmed shows loaded:', shows);
        setConfirmedShows(shows);
      } else {
        console.warn('⚠️ No confirmed shows found');
        setConfirmedShows([]); // Set empty array to show "no shows" message
      }
    } catch (error) {
      console.error('❌ Error fetching confirmed shows:', error);
      console.error('Error details:', error.response?.data || error.message);
      setConfirmedShows([]); // Set empty array to show error state
    } finally {
      setIsLoadingConfirmedShows(false);
    }
  };

  return (
    <Container>
      {/* Fixed Header */}
      <div className="fixed top-0 w-full flex justify-center bg-black border border-black" style={{ zIndex: 10010 }}>
        <div style={{ display: 'flex', width: '358px' }}>
          <HeaderBar 
            isHome={true} 
            activeTab={activeTab}
            isAppsViewExpanded={isAppsExpanded}
            onAppsViewToggle={handleAppsViewToggle}
          />
        </div>
      </div>

      <MainContent>
        {/* Main Navigation Tabs - Full Width Outside Container */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          gap: '0',
          width: '100%',
          maxWidth: '390px',
          margin: '0 auto 15px',
          marginTop: '10px',
          padding: '0 5%',
          flexShrink: 0,
          position: 'relative',
          zIndex: 10
        }}>
          <MainTabButton onClick={() => handleTabChange('chat')} active={activeTab === 'chat'} align="left">
            Chat
          </MainTabButton>
          <MainTabButton onClick={() => handleTabChange('activity')} active={activeTab === 'activity'} align="center">
            Timeline
          </MainTabButton>
          <MainTabButton onClick={() => handleTabChange('apps')} active={activeTab === 'apps'} align="right">
            Apps
          </MainTabButton>
        </div>
        
        <OptionsContainer 
          className='relative' 
          isActivityTab={activeTab === 'activity'}
          style={activeTab === 'activity' ? { height: 'calc(100vh - 240px)', maxHeight: 'calc(100vh - 240px)' } : {}}
        >
          
          {/* Chat Section with Orb - Only shown when activeTab is 'chat' */}
          {activeTab === 'chat' && (
            <>
              {/* Bottom Glow - Only shown when maximized and typing */}
              <BottomGlow isVisible={isChatMaximized && (isTyping || isThinking)} />
              
              <ChatContainer
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: "easeOut" }}
                isMaximized={isChatMaximized}
                data-chat-container
              >
                {/* Orb - Only shown when not maximized */}
                {!isChatMaximized && (
                  <OrbWrapper isTyping={isTyping || isThinking}>
                    <OrbContainer
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.6, ease: "easeOut" }}
                    >
                      <OrangeOrb3D />
                    </OrbContainer>
                  </OrbWrapper>
                )}
                
                  <ConversationArea 
                    ref={conversationRef} 
                    isMaximized={isChatMaximized}
                  style={{
                    opacity: isFadingOut ? 0 : ((chatMessages.length > 0 || typingBuffer || isThinking) ? 1 : 0),
                    pointerEvents: (chatMessages.length > 0 || typingBuffer || isThinking) && !isFadingOut ? 'auto' : 'none',
                    // Keep height/padding during fade to prevent layout shift
                    minHeight: isFadingOut ? conversationRef.current?.scrollHeight || 'auto' : ((chatMessages.length > 0 || typingBuffer || isThinking) ? 'auto' : '0'),
                    height: isFadingOut ? conversationRef.current?.scrollHeight || 'auto' : ((chatMessages.length > 0 || typingBuffer || isThinking) ? 'auto' : '0'),
                    maxHeight: isFadingOut ? conversationRef.current?.scrollHeight || 'auto' : ((chatMessages.length > 0 || typingBuffer || isThinking) ? undefined : '0'),
                    padding: isFadingOut ? '20px' : ((chatMessages.length > 0 || typingBuffer || isThinking) ? undefined : '0'),
                    margin: (chatMessages.length > 0 || typingBuffer || isThinking) && !isFadingOut ? undefined : '0',
                    gap: isFadingOut ? '24px' : ((chatMessages.length > 0 || typingBuffer || isThinking) ? undefined : '0'),
                    overflow: 'hidden', // Always hidden during transition to prevent scrollbar flash
                    transition: isFadingOut ? 'opacity 0.3s ease' : 'opacity 0.3s ease, min-height 0.3s ease, height 0.3s ease, max-height 0.3s ease, padding 0.3s ease, margin 0.3s ease, gap 0.3s ease'
                  }}
                  onTransitionEnd={(e) => {
                    if (e.propertyName === 'max-height') {
                      console.log('🔄 [LANDING] ConversationArea max-height transition ended');
                      requestAnimationFrame(() => {
                        const footer = document.querySelector('[data-footer="homepage"]');
                        if (footer) {
                          const rect = footer.getBoundingClientRect();
                          console.log('📍 [LANDING] Footer position after ConversationArea transition:', {
                            top: rect.top,
                            bottom: rect.bottom,
                            height: rect.height,
                            y: rect.y
                          });
                        }
                      });
                    }
                  }}
                  >
                    {/* Show all messages */}
                    {chatMessages.map((m, i) => {
                      const isLastMessage = i === chatMessages.length - 1;
                      
                      // Skip rendering last assistant message if typing buffer exists OR if typing is in progress
                      if (isLastMessage && m.role === 'assistant' && (typingBuffer || isTyping || isThinking)) {
                        return null;
                      }
                      
                      // Check if this is a checklist message - render it specially
                      if (m.isChecklistMessage && m.role === 'assistant') {
                        console.log('✅ Rendering checklist UI for message:', m);
                        const MessageComponent = m.isNavigationMessage ? NavigationMessage : AIMessage;
                        
                        return (
                          <div key={i} style={{ width: '100%' }}>
                            {/* Welcome text in message bubble */}
                            <MessageComponent 
                              role={m.role}
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ duration: 0.4, ease: "easeOut" }}
                            >
                              <div>
                                {formatTextWithBold(m.text)}
                              </div>
                            </MessageComponent>

                            {/* Orange instruction text */}
                            <div style={{
                              width: '100%',
                              marginTop: '12px',
                              marginBottom: '8px',
                              paddingLeft: '0'
                            }}>
                              <div style={{
                                fontSize: '13px',
                                color: '#FF2300',
                                fontWeight: '500',
                                lineHeight: '1.5'
                              }}>
                                Click on the orange circles below to confirm that you've completed each part of the checklist
                              </div>
                            </div>

                            {/* Full-width checklist UI outside message bubble */}
                            <div style={{
                              width: '100%',
                              marginTop: '16px',
                              marginBottom: '16px'
                            }}>
                              {/* Progress Bar */}
                              <div style={{
                                margin: '20px 0',
                                padding: '16px',
                                backgroundColor: 'rgba(255, 255, 255, 0.03)',
                                borderRadius: '12px',
                                border: '1px solid rgba(255, 255, 255, 0.08)',
                                width: '100%',
                                boxSizing: 'border-box'
                              }}>
                                <div style={{
                                  width: '100%',
                                  height: '8px',
                                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                  borderRadius: '4px',
                                  overflow: 'hidden',
                                  marginBottom: '8px'
                                }}>
                                  <div style={{
                                    height: '100%',
                                    width: `${checklistPercentage}%`,
                                    background: 'linear-gradient(90deg, #FF2300 0%, #FF4520 100%)',
                                    borderRadius: '4px',
                                    transition: 'width 0.3s ease'
                                  }} />
                                </div>
                                <div style={{
                                  fontSize: '13px',
                                  color: '#FF2300',
                                  fontWeight: '600',
                                  textAlign: 'right'
                                }}>
                                  {checklistPercentage}% Completed
                                </div>
                              </div>

                              {/* Checklist Items */}
                              <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '10px',
                                margin: '20px 0',
                                width: '100%'
                              }}>
                                {CHECKLIST_ITEMS_CONFIG.map((item) => {
                                  const isCompleted = checklistItems.includes(item.id);
                                  
                                  return (
                                    <div 
                                      key={item.id} 
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '12px',
                                        padding: '14px 16px',
                                        width: '100%',
                                        backgroundColor: isCompleted ? 'rgba(52, 211, 153, 0.05)' : 'rgba(255, 255, 255, 0.03)',
                                        border: `1.5px solid ${isCompleted ? 'rgba(52, 211, 153, 0.5)' : 'rgba(255, 35, 0, 0.3)'}`,
                                        borderRadius: '8px',
                                        transition: 'all 0.2s ease',
                                        boxSizing: 'border-box'
                                      }}
                                    >
                                      <button
                                        onClick={() => handleChecklistToggle(item.id)}
                                        style={{
                                          width: '24px',
                                          height: '24px',
                                          minWidth: '24px',
                                          border: `2px solid ${isCompleted ? '#34D399' : '#FF2300'}`,
                                          borderRadius: '50%',
                                          background: isCompleted ? '#34D399' : 'transparent',
                                          cursor: 'pointer',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          transition: 'all 0.2s ease',
                                          padding: 0,
                                          color: '#FFFFFF',
                                          fontSize: '14px',
                                          fontWeight: '600'
                                        }}
                                        aria-label={`${isCompleted ? 'Unmark' : 'Mark'} ${item.label} as complete`}
                                      >
                                        {isCompleted ? '✓' : ''}
                                      </button>
                                      <div style={{
                                        flex: 1,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '4px'
                                      }}>
                                        <span style={{
                                          fontSize: '14px',
                                          color: 'rgba(255, 255, 255, 0.9)',
                                          fontWeight: '500',
                                          textDecoration: isCompleted ? 'line-through' : 'none',
                                          opacity: isCompleted ? 0.6 : 1
                                        }}>
                                          {item.label}
                                        </span>
                                        <span style={{
                                          fontSize: '12px',
                                          color: 'rgba(255, 255, 255, 0.5)',
                                          fontWeight: '400'
                                        }}>
                                          {item.description}
                                        </span>
                                      </div>
                                      {item.route && (
                                        <button
                                          onClick={() => handleChecklistNavigate(item.route)}
                                          style={{
                                            background: 'transparent',
                                            border: '1px solid rgba(255, 255, 255, 0.2)',
                                            borderRadius: '6px',
                                            color: 'rgba(255, 255, 255, 0.7)',
                                            fontSize: '16px',
                                            width: '32px',
                                            height: '32px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease',
                                            padding: 0
                                          }}
                                          title={`Go to ${item.label}`}
                                        >
                                          →
                                        </button>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>

                              {/* Complete Button */}
                              <div style={{
                                marginTop: '24px',
                                display: 'flex',
                                justifyContent: 'center',
                                width: '100%'
                              }}>
                                <button
                                  onClick={handleChecklistComplete}
                                  style={{
                                    width: '100%',
                                    border: 'none',
                                    borderRadius: '8px',
                                    background: 'linear-gradient(135deg, #FF2300 0%, #FF4520 100%)',
                                    color: '#FFFFFF',
                                    fontSize: '14px',
                                    fontWeight: '600',
                                    padding: '12px 16px',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.target.style.transform = 'scale(1.02)';
                                    e.target.style.boxShadow = '0 0 25px rgba(255, 35, 0, 0.4)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.target.style.transform = 'scale(1)';
                                    e.target.style.boxShadow = 'none';
                                  }}
                                >
                                  ✓ Complete Setup Checklist
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      }
                      
                      // Use NavigationMessage component for navigation messages
                      const MessageComponent = m.isNavigationMessage ? NavigationMessage : AIMessage;
                      
                      return (
                        <MessageComponent 
                          key={i} 
                          role={m.role}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.4, ease: "easeOut" }}
                        >
                          {(() => {
                            
                            // Check if this message contains a catalog form marker at the beginning
                            // ONLY show catalog form for ASSISTANT messages, not user messages
                            const hasCatalogForm = m.role === 'assistant' && m.text && (m.text.startsWith('[CATALOG_FORM:release]') || m.text.startsWith('[CATALOG_FORM:demo]'));
                            
                            if (hasCatalogForm) {
                              // Check if form was already submitted (next message is user confirming they added something)
                              const nextMessage = chatMessages[i + 1];
                              const formWasSubmitted = nextMessage && 
                                nextMessage.role === 'user' && 
                                nextMessage.text && 
                                nextMessage.text.includes('I just added a new');
                              
                              console.log('🔍 [FORM DETECTION] Form submitted?', formWasSubmitted);
                              
                              // If form was already submitted, just show the AI's intro text (no form needed)
                              if (formWasSubmitted) {
                                const cleanText = m.text.replace(/^\[CATALOG_FORM:(release|demo)\]\s*/g, '').trim();
                                
                                return (
                                  <div>
                                    {cleanText.split('\n').map((line, lineIdx) => {
                                      const isBullet = line.trim().startsWith('•');
                                      
                                      if (isBullet) {
                                        const text = line.replace('•', '').trim();
                                        return (
                                          <div key={lineIdx}>
                                            <span className="orange-bullet">• </span>
                                            <span>{formatTextWithBold(text)}</span>
                                          </div>
                                        );
                                      }
                                      
                                      if (!line.trim()) {
                                        return <div key={lineIdx}>&nbsp;</div>;
                                      }
                                      
                                      return <div key={lineIdx}>{formatTextWithBold(line)}</div>;
                                    })}
                                  </div>
                                );
                              }
                              
                              // Form hasn't been submitted yet, show the form
                              const cleanText = m.text.replace(/^\[CATALOG_FORM:(release|demo)\]\s*/g, '').trim();
                              const formType = m.text.startsWith('[CATALOG_FORM:demo]') ? 'demo' : 'release';
                              
                              return (
                                <>
                                  {/* Display the AI's message text */}
                                  {cleanText && (
                                    <div style={{ marginBottom: '16px' }}>
                                      {cleanText.split('\n').map((line, lineIdx) => {
                                        const isBullet = line.trim().startsWith('•');
                                        
                                        if (isBullet) {
                                          const text = line.replace('•', '').trim();
                                          return (
                                            <div key={lineIdx}>
                                              <span className="orange-bullet">• </span>
                                              <span>{formatTextWithBold(text)}</span>
                                            </div>
                                          );
                                        }
                                        
                                        if (!line.trim()) {
                                          return <div key={lineIdx}>&nbsp;</div>;
                                        }
                                        
                                        return <div key={lineIdx}>{formatTextWithBold(line)}</div>;
                                      })}
                                    </div>
                                  )}
                                  
                                  {/* Render the catalog form inline */}
                                  {!catalogFormType && (() => {
                                    // Initialize form on first render
                                    setCatalogFormType(formType);
                                    setCatalogFormData(prev => ({
                                      ...prev,
                                      releaseStatus: formType === 'demo' ? 'demo' : 'upcoming'
                                    }));
                                    return null;
                                  })()}
                                  
                                  <div style={{
                                    backgroundColor: '#000000',
                                    border: '1px solid #19a2b8',
                                    borderRadius: '8px',
                                    padding: '16px',
                                    marginTop: '12px',
                                    width: 'calc(100% / 0.75)',
                                    maxWidth: 'none',
                                    boxSizing: 'border-box',
                                    marginLeft: '0',
                                    marginRight: 'calc(-33.33% - 20px)',
                                    position: 'relative'
                                  }}>
                                    <div style={{ color: '#19a2b8', fontSize: '14px', fontWeight: '600', marginBottom: '16px' }}>
                                      {formType === 'demo' ? 'Add Demo Track' : 'Add Upcoming Release'}
                                    </div>
                                    
                                    {/* Song Title */}
                                    <div style={{ marginBottom: '14px' }}>
                                      <label style={{ display: 'block', color: 'white', fontSize: '12px', fontWeight: '500', marginBottom: '6px' }}>
                                        Song Title <span style={{ color: '#FF3B30' }}>*</span>
                                      </label>
                                      <input
                                        type="text"
                                        placeholder="Enter song title"
                                        value={catalogFormData.songTitle}
                                        onChange={(e) => setCatalogFormData(prev => ({ ...prev, songTitle: e.target.value }))}
                                        disabled={isSubmittingForm}
                                        style={{
                                          width: '100%',
                                          backgroundColor: '#111111',
                                          border: '1px solid #2A2A2A',
                                          borderRadius: '6px',
                                          color: 'white',
                                          fontSize: '13px',
                                          padding: '10px',
                                          outline: 'none',
                                          boxSizing: 'border-box'
                                        }}
                                      />
                                    </div>
                                    
                                    {/* Primary Artists */}
                                    <div style={{ marginBottom: '14px' }}>
                                      <label style={{ display: 'block', color: 'white', fontSize: '12px', fontWeight: '500', marginBottom: '6px' }}>
                                        Primary Artists <span style={{ color: '#FF3B30' }}>*</span>
                                      </label>
                                      {catalogFormData.primaryArtists.map((artist, index) => (
                                        <div key={index} style={{ display: 'flex', gap: '6px', marginBottom: '6px' }}>
                                          <input
                                            type="text"
                                            placeholder={`Primary artist ${index + 1}`}
                                            value={artist}
                                            onChange={(e) => updatePrimaryArtist(index, e.target.value)}
                                            disabled={isSubmittingForm}
                                            style={{
                                              flex: 1,
                                              backgroundColor: '#111111',
                                              border: '1px solid #2A2A2A',
                                              borderRadius: '6px',
                                              color: 'white',
                                              fontSize: '13px',
                                              padding: '10px',
                                              outline: 'none'
                                            }}
                                          />
                                          {catalogFormData.primaryArtists.length > 1 && (
                                            <button
                                              onClick={() => removePrimaryArtist(index)}
                                              disabled={isSubmittingForm}
                                              style={{
                                                backgroundColor: 'transparent',
                                                border: '1px solid #2A2A2A',
                                                borderRadius: '6px',
                                                color: '#8E8E93',
                                                fontSize: '14px',
                                                padding: '8px 12px',
                                                cursor: isSubmittingForm ? 'not-allowed' : 'pointer',
                                                minWidth: '36px'
                                              }}
                                            >
                                              ×
                                            </button>
                                          )}
                                        </div>
                                      ))}
                                      {catalogFormData.primaryArtists.length < 6 && (
                                        <button
                                          onClick={addPrimaryArtist}
                                          disabled={isSubmittingForm}
                                          style={{
                                            backgroundColor: 'transparent',
                                            border: '1px solid #2A2A2A',
                                            borderRadius: '6px',
                                            color: '#19a2b8',
                                            fontSize: '12px',
                                            padding: '8px 12px',
                                            cursor: isSubmittingForm ? 'not-allowed' : 'pointer',
                                            marginTop: '4px'
                                          }}
                                        >
                                          + Add Primary Artist
                                        </button>
                                      )}
                                    </div>
                                    
                                    {/* Featured Artists */}
                                    <div style={{ marginBottom: '14px' }}>
                                      <label style={{ display: 'block', color: 'white', fontSize: '12px', fontWeight: '500', marginBottom: '6px' }}>
                                        Featured Artists (Optional)
                                      </label>
                                      {catalogFormData.featuredArtists.map((artist, index) => (
                                        <div key={index} style={{ display: 'flex', gap: '6px', marginBottom: '6px' }}>
                                          <input
                                            type="text"
                                            placeholder={`Featured artist ${index + 1}`}
                                            value={artist}
                                            onChange={(e) => updateFeaturedArtist(index, e.target.value)}
                                            disabled={isSubmittingForm}
                                            style={{
                                              flex: 1,
                                              backgroundColor: '#111111',
                                              border: '1px solid #2A2A2A',
                                              borderRadius: '6px',
                                              color: 'white',
                                              fontSize: '13px',
                                              padding: '10px',
                                              outline: 'none'
                                            }}
                                          />
                                          {catalogFormData.featuredArtists.length > 1 && (
                                            <button
                                              onClick={() => removeFeaturedArtist(index)}
                                              disabled={isSubmittingForm}
                                              style={{
                                                backgroundColor: 'transparent',
                                                border: '1px solid #2A2A2A',
                                                borderRadius: '6px',
                                                color: '#8E8E93',
                                                fontSize: '14px',
                                                padding: '8px 12px',
                                                cursor: isSubmittingForm ? 'not-allowed' : 'pointer',
                                                minWidth: '36px'
                                              }}
                                            >
                                              ×
                                            </button>
                                          )}
                                        </div>
                                      ))}
                                      {catalogFormData.featuredArtists.length < 6 && (
                                        <button
                                          onClick={addFeaturedArtist}
                                          disabled={isSubmittingForm}
                                          style={{
                                            backgroundColor: 'transparent',
                                            border: '1px solid #2A2A2A',
                                            borderRadius: '6px',
                                            color: '#19a2b8',
                                            fontSize: '12px',
                                            padding: '8px 12px',
                                            cursor: isSubmittingForm ? 'not-allowed' : 'pointer',
                                            marginTop: '4px'
                                          }}
                                        >
                                          + Add Featured Artist
                                        </button>
                                      )}
                                    </div>
                                    
                                    {/* Release Status (only for releases, not demos) */}
                                    {formType === 'release' && (
                                      <div style={{ marginBottom: '14px' }}>
                                        <label style={{ display: 'block', color: 'white', fontSize: '12px', fontWeight: '500', marginBottom: '6px' }}>
                                          Release Status <span style={{ color: '#FF3B30' }}>*</span>
                                        </label>
                                        <select
                                          value={catalogFormData.releaseStatus}
                                          onChange={(e) => setCatalogFormData(prev => ({ ...prev, releaseStatus: e.target.value }))}
                                          disabled={isSubmittingForm}
                                          style={{
                                            width: '100%',
                                            backgroundColor: '#111111',
                                            border: '1px solid #2A2A2A',
                                            borderRadius: '6px',
                                            color: 'white',
                                            fontSize: '13px',
                                            padding: '10px',
                                            outline: 'none',
                                            boxSizing: 'border-box'
                                          }}
                                        >
                                          <option value="upcoming">Upcoming</option>
                                          <option value="released">Released</option>
                                        </select>
                                      </div>
                                    )}
                                    
                                    {/* Release Date */}
                                    <div style={{ marginBottom: '14px' }}>
                                      <label style={{ display: 'block', color: 'white', fontSize: '12px', fontWeight: '500', marginBottom: '6px' }}>
                                        Release Date (Optional)
                                      </label>
                                      <input
                                        type="date"
                                        value={catalogFormData.releaseDate}
                                        onChange={(e) => setCatalogFormData(prev => ({ ...prev, releaseDate: e.target.value }))}
                                        disabled={isSubmittingForm}
                                        style={{
                                          width: '100%',
                                          backgroundColor: '#111111',
                                          border: '1px solid #2A2A2A',
                                          borderRadius: '6px',
                                          color: 'white',
                                          fontSize: '13px',
                                          padding: '10px',
                                          outline: 'none',
                                          boxSizing: 'border-box',
                                          colorScheme: 'dark'
                                        }}
                                      />
                                    </div>
                                    
                                    {/* Additional Info */}
                                    <div style={{ marginBottom: '18px' }}>
                                      <label style={{ display: 'block', color: 'white', fontSize: '12px', fontWeight: '500', marginBottom: '6px' }}>
                                        Additional Info (Optional)
                                      </label>
                                      <textarea
                                        placeholder="Any additional details about this track..."
                                        value={catalogFormData.additionalInfo}
                                        onChange={(e) => setCatalogFormData(prev => ({ ...prev, additionalInfo: e.target.value }))}
                                        disabled={isSubmittingForm}
                                        style={{
                                          width: '100%',
                                          backgroundColor: '#111111',
                                          border: '1px solid #2A2A2A',
                                          borderRadius: '6px',
                                          color: 'white',
                                          fontSize: '13px',
                                          padding: '10px',
                                          outline: 'none',
                                          resize: 'vertical',
                                          minHeight: '70px',
                                          boxSizing: 'border-box'
                                        }}
                                      />
                                    </div>
                                    
                                    {/* Form Buttons */}
                                    <div style={{ display: 'flex', gap: '10px' }}>
                                      <button
                                        onClick={handleCatalogFormCancel}
                                        disabled={isSubmittingForm}
                                        style={{
                                          flex: 1,
                                          backgroundColor: 'transparent',
                                          border: '1px solid #2A2A2A',
                                          borderRadius: '6px',
                                          color: '#8E8E93',
                                          fontSize: '13px',
                                          padding: '12px',
                                          cursor: isSubmittingForm ? 'not-allowed' : 'pointer',
                                          fontWeight: '500'
                                        }}
                                      >
                                        Cancel
                                      </button>
                                      <button
                                        onClick={handleCatalogFormSubmit}
                                        disabled={isSubmittingForm}
                                        style={{
                                          flex: 1,
                                          backgroundColor: isSubmittingForm ? '#555' : '#19a2b8',
                                          border: 'none',
                                          borderRadius: '6px',
                                          color: 'white',
                                          fontSize: '13px',
                                          padding: '12px',
                                          cursor: isSubmittingForm ? 'not-allowed' : 'pointer',
                                          fontWeight: '600'
                                        }}
                                      >
                                        {isSubmittingForm ? 'Adding...' : 'Add to Catalog'}
                                      </button>
                                    </div>
                                  </div>
                                </>
                              );
                            }
                            
                            // Check if this message contains a booking form marker at the beginning
                            // ONLY show booking form for ASSISTANT messages, not user messages
                            const hasBookingForm = m.role === 'assistant' && m.text && m.text.startsWith('[BOOKING_FORM:show]');
                            
                            if (hasBookingForm) {
                              // Check if form was already submitted
                              const nextMessage = chatMessages[i + 1];
                              const formWasSubmitted = nextMessage && 
                                nextMessage.role === 'user' && 
                                nextMessage.text && 
                                nextMessage.text.includes('I just added a new show');
                              
                              console.log('🔍 [BOOKING FORM DETECTION] Form submitted?', formWasSubmitted);
                              
                              // If form was already submitted, just show the AI's intro text
                              if (formWasSubmitted) {
                                const cleanText = m.text.replace(/^\[BOOKING_FORM:show\]\s*/g, '').trim();
                                
                                return (
                                  <div>
                                    {cleanText.split('\n').map((line, lineIdx) => {
                                      const isBullet = line.trim().startsWith('•');
                                      
                                      if (isBullet) {
                                        const text = line.replace('•', '').trim();
                                        return (
                                          <div key={lineIdx}>
                                            <span className="orange-bullet">• </span>
                                            <span>{formatTextWithBold(text)}</span>
                                          </div>
                                        );
                                      }
                                      
                                      if (!line.trim()) {
                                        return <div key={lineIdx}>&nbsp;</div>;
                                      }
                                      
                                      return <div key={lineIdx}>{formatTextWithBold(line)}</div>;
                                    })}
                                  </div>
                                );
                              }
                              
                              // Form hasn't been submitted yet, show the form
                              const cleanText = m.text.replace(/^\[BOOKING_FORM:show\]\s*/g, '').trim();
                              
                              // Initialize form state if not already set
                              if (!bookingFormType) {
                                console.log('🎫 Initializing booking form state');
                                setBookingFormType('show');
                              }
                              
                              return (
                                <>
                                  {/* Display the AI's message text */}
                                  {cleanText && (
                                    <div style={{ marginBottom: '16px' }}>
                                      {cleanText.split('\n').map((line, lineIdx) => {
                                        const isBullet = line.trim().startsWith('•');
                                        
                                        if (isBullet) {
                                          const text = line.replace('•', '').trim();
                                          return (
                                            <div key={lineIdx}>
                                              <span className="orange-bullet">• </span>
                                              <span>{formatTextWithBold(text)}</span>
                                            </div>
                                          );
                                        }
                                        
                                        if (!line.trim()) {
                                          return <div key={lineIdx}>&nbsp;</div>;
                                        }
                                        
                                        return <div key={lineIdx}>{formatTextWithBold(line)}</div>;
                                      })}
                                    </div>
                                  )}
                                  
                                  {/* Booking Form */}
                                  <div style={{
                                    backgroundColor: '#000000',
                                    border: '2px solid #ffff05',
                                    borderRadius: '12px',
                                    padding: '20px',
                                    marginTop: '12px',
                                    width: 'calc(100% / 0.75)',
                                    maxWidth: 'none',
                                    boxSizing: 'border-box',
                                    marginLeft: '0',
                                    marginRight: 'calc(-33.33% - 20px)',
                                    position: 'relative'
                                  }}>
                                    <div style={{ 
                                      color: '#ffff05', 
                                      fontSize: '16px', 
                                      fontWeight: '600', 
                                      marginBottom: '16px',
                                      paddingBottom: '12px',
                                      borderBottom: '1px solid #2A2A2A'
                                    }}>
                                      Add Show Details
                                    </div>
                                    
                                    {/* Venue */}
                                    <div style={{ marginBottom: '14px' }}>
                                      <label style={{ display: 'block', color: 'white', fontSize: '12px', fontWeight: '500', marginBottom: '6px' }}>
                                        Venue <span style={{ color: '#FF3B30' }}>*</span>
                                      </label>
                                      <input
                                        type="text"
                                        value={bookingFormData.venue}
                                        onChange={(e) => setBookingFormData(prev => ({ ...prev, venue: e.target.value }))}
                                        disabled={isSubmittingBookingForm}
                                        placeholder="e.g., Madison Square Garden"
                                        style={{
                                          width: '100%',
                                          backgroundColor: '#111111',
                                          border: '1px solid #2A2A2A',
                                          borderRadius: '6px',
                                          color: 'white',
                                          fontSize: '13px',
                                          padding: '10px',
                                          outline: 'none',
                                          boxSizing: 'border-box'
                                        }}
                                      />
                                    </div>
                                    
                                    {/* Artist */}
                                    <div style={{ marginBottom: '14px' }}>
                                      <label style={{ display: 'block', color: 'white', fontSize: '12px', fontWeight: '500', marginBottom: '6px' }}>
                                        Artist <span style={{ color: '#FF3B30' }}>*</span>
                                      </label>
                                      <input
                                        type="text"
                                        value={bookingFormData.artist}
                                        onChange={(e) => setBookingFormData(prev => ({ ...prev, artist: e.target.value }))}
                                        disabled={isSubmittingBookingForm}
                                        placeholder="Artist name"
                                        style={{
                                          width: '100%',
                                          backgroundColor: '#111111',
                                          border: '1px solid #2A2A2A',
                                          borderRadius: '6px',
                                          color: 'white',
                                          fontSize: '13px',
                                          padding: '10px',
                                          outline: 'none',
                                          boxSizing: 'border-box'
                                        }}
                                      />
                                    </div>
                                    
                                    {/* Performance Date */}
                                    <div style={{ marginBottom: '14px' }}>
                                      <label style={{ display: 'block', color: 'white', fontSize: '12px', fontWeight: '500', marginBottom: '6px' }}>
                                        Performance Date <span style={{ color: '#FF3B30' }}>*</span>
                                      </label>
                                      <input
                                        type="date"
                                        value={bookingFormData.perfDate}
                                        onChange={(e) => setBookingFormData(prev => ({ ...prev, perfDate: e.target.value }))}
                                        disabled={isSubmittingBookingForm}
                                        style={{
                                          width: '100%',
                                          backgroundColor: '#111111',
                                          border: '1px solid #2A2A2A',
                                          borderRadius: '6px',
                                          color: 'white',
                                          fontSize: '13px',
                                          padding: '10px',
                                          outline: 'none',
                                          boxSizing: 'border-box',
                                          colorScheme: 'dark'
                                        }}
                                      />
                                    </div>
                                    
                                    {/* City */}
                                    <div style={{ marginBottom: '14px' }}>
                                      <label style={{ display: 'block', color: 'white', fontSize: '12px', fontWeight: '500', marginBottom: '6px' }}>
                                        City <span style={{ color: '#FF3B30' }}>*</span>
                                      </label>
                                      <input
                                        type="text"
                                        value={bookingFormData.city}
                                        onChange={(e) => setBookingFormData(prev => ({ ...prev, city: e.target.value }))}
                                        disabled={isSubmittingBookingForm}
                                        placeholder="e.g., New York"
                                        style={{
                                          width: '100%',
                                          backgroundColor: '#111111',
                                          border: '1px solid #2A2A2A',
                                          borderRadius: '6px',
                                          color: 'white',
                                          fontSize: '13px',
                                          padding: '10px',
                                          outline: 'none',
                                          boxSizing: 'border-box'
                                        }}
                                      />
                                    </div>
                                    
                                    {/* State */}
                                    <div style={{ marginBottom: '14px' }}>
                                      <label style={{ display: 'block', color: 'white', fontSize: '12px', fontWeight: '500', marginBottom: '6px' }}>
                                        State/Province <span style={{ color: '#FF3B30' }}>*</span>
                                      </label>
                                      <input
                                        type="text"
                                        value={bookingFormData.state}
                                        onChange={(e) => setBookingFormData(prev => ({ ...prev, state: e.target.value }))}
                                        disabled={isSubmittingBookingForm}
                                        placeholder="e.g., NY"
                                        style={{
                                          width: '100%',
                                          backgroundColor: '#111111',
                                          border: '1px solid #2A2A2A',
                                          borderRadius: '6px',
                                          color: 'white',
                                          fontSize: '13px',
                                          padding: '10px',
                                          outline: 'none',
                                          boxSizing: 'border-box'
                                        }}
                                      />
                                    </div>
                                    
                                    {/* Additional Info */}
                                    <div style={{ marginBottom: '18px' }}>
                                      <label style={{ display: 'block', color: 'white', fontSize: '12px', fontWeight: '500', marginBottom: '6px' }}>
                                        Additional Info (Optional)
                                      </label>
                                      <textarea
                                        value={bookingFormData.additionalInfo}
                                        onChange={(e) => setBookingFormData(prev => ({ ...prev, additionalInfo: e.target.value }))}
                                        disabled={isSubmittingBookingForm}
                                        placeholder="Add any notes about this show..."
                                        rows={3}
                                        style={{
                                          width: '100%',
                                          backgroundColor: '#111111',
                                          border: '1px solid #2A2A2A',
                                          borderRadius: '6px',
                                          color: 'white',
                                          fontSize: '13px',
                                          padding: '10px',
                                          outline: 'none',
                                          boxSizing: 'border-box',
                                          resize: 'vertical',
                                          fontFamily: 'inherit'
                                        }}
                                      />
                                    </div>
                                    
                                    {/* Form Buttons */}
                                    <div style={{ display: 'flex', gap: '10px' }}>
                                      <button
                                        onClick={handleBookingFormCancel}
                                        disabled={isSubmittingBookingForm}
                                        style={{
                                          flex: 1,
                                          backgroundColor: 'transparent',
                                          border: '1px solid #2A2A2A',
                                          borderRadius: '6px',
                                          color: '#8E8E93',
                                          fontSize: '13px',
                                          padding: '12px',
                                          cursor: isSubmittingBookingForm ? 'not-allowed' : 'pointer',
                                          fontWeight: '500'
                                        }}
                                      >
                                        Cancel
                                      </button>
                                      <button
                                        onClick={handleBookingFormSubmit}
                                        disabled={isSubmittingBookingForm}
                                        style={{
                                          flex: 1,
                                          backgroundColor: isSubmittingBookingForm ? '#555' : '#ffff05',
                                          border: 'none',
                                          borderRadius: '6px',
                                          color: '#000',
                                          fontSize: '13px',
                                          padding: '12px',
                                          cursor: isSubmittingBookingForm ? 'not-allowed' : 'pointer',
                                          fontWeight: '600'
                                        }}
                                      >
                                        {isSubmittingBookingForm ? 'Adding...' : 'Add Show'}
                                      </button>
                                    </div>
                                  </div>
                                </>
                              );
                            }

                            // Check if this message contains a task form marker at the beginning
                            // ONLY show task form for ASSISTANT messages, not user messages
                            const hasTaskForm = m.role === 'assistant' && m.text && m.text.startsWith('[TASK_FORM:new]');

                            if (hasTaskForm) {
                              // Check if form was already submitted
                              const nextMessage = chatMessages[i + 1];
                              const formWasSubmitted = nextMessage && 
                                nextMessage.role === 'user' && 
                                nextMessage.text && 
                                nextMessage.text.includes('I just created a new task');
                              
                              console.log('🔍 [TASK FORM DETECTION] Form submitted?', formWasSubmitted);
                              
                              // If form was already submitted, just show the AI's intro text
                              if (formWasSubmitted) {
                                const cleanText = m.text.replace(/^\[TASK_FORM:new\]\s*/g, '').trim();
                                
                                return (
                                  <div>
                                    {cleanText.split('\n').map((line, lineIdx) => {
                                      const isBullet = line.trim().startsWith('•');
                                      
                                      if (isBullet) {
                                        const text = line.replace('•', '').trim();
                                        return (
                                          <div key={lineIdx}>
                                            <span className="orange-bullet">• </span>
                                            <span>{formatTextWithBold(text)}</span>
                                          </div>
                                        );
                                      }
                                      
                                      if (!line.trim()) {
                                        return <div key={lineIdx}>&nbsp;</div>;
                                      }
                                      
                                      return <div key={lineIdx}>{formatTextWithBold(line)}</div>;
                                    })}
                                  </div>
                                );
                              }
                              
                              // Form hasn't been submitted yet, show the form
                              const cleanText = m.text.replace(/^\[TASK_FORM:new\]\s*/g, '').trim();
                              
                              // Initialize form state if not already set
                              if (!taskFormType) {
                                console.log('📝 Initializing task form state');
                                setTaskFormType('new');
                                // Fetch team members when form is initialized
                                const fetchTeamMembers = async () => {
                                  setLoadingTeam(true);
                                  try {
                                    const members = await getTeamMembersForTasks();
                                    const formattedMembers = members.map(member => ({
                                      id: member._id,
                                      name: member.name,
                                      role: member.role,
                                      email: member.email,
                                      _id: member._id
                                    }));
                                    setTeamMembers(formattedMembers);
                                  } catch (error) {
                                    console.error('Error fetching team members:', error);
                                    // Fallback to just current user
                                    const currentUser = {
                                      id: userArticle?._id || 'self',
                                      name: `${userArticle?.username || userArticle?.email || 'Me'} (me)`,
                                      role: userArticle?.professionRole || 'Self',
                                      email: userArticle?.email,
                                      _id: userArticle?._id
                                    };
                                    setTeamMembers([currentUser]);
                                  } finally {
                                    setLoadingTeam(false);
                                  }
                                };
                                fetchTeamMembers();
                              }
                              
                              return (
                                <>
                                  {/* Display the AI's message text */}
                                  {cleanText && (
                                    <div style={{ marginBottom: '16px' }}>
                                      {cleanText.split('\n').map((line, lineIdx) => {
                                        const isBullet = line.trim().startsWith('•');
                                        
                                        if (isBullet) {
                                          const text = line.replace('•', '').trim();
                                          return (
                                            <div key={lineIdx}>
                                              <span className="orange-bullet">• </span>
                                              <span>{formatTextWithBold(text)}</span>
                                            </div>
                                          );
                                        }
                                        
                                        if (!line.trim()) {
                                          return <div key={lineIdx}>&nbsp;</div>;
                                        }
                                        
                                        return <div key={lineIdx}>{formatTextWithBold(line)}</div>;
                                      })}
                                    </div>
                                  )}
                                  
                                  {/* Task Form */}
                                  <div style={{
                                    backgroundColor: '#000000',
                                    border: '2px solid #FF6B35',
                                    borderRadius: '12px',
                                    padding: '20px',
                                    marginTop: '12px',
                                    width: 'calc(100% / 0.75)',
                                    maxWidth: 'none',
                                    boxSizing: 'border-box',
                                    marginLeft: '0',
                                    marginRight: 'calc(-33.33% - 20px)',
                                    position: 'relative'
                                  }}>
                                    <div style={{ 
                                      color: '#FF6B35', 
                                      fontSize: '16px', 
                                      fontWeight: '600', 
                                      marginBottom: '16px',
                                      paddingBottom: '12px',
                                      borderBottom: '1px solid #2A2A2A'
                                    }}>
                                      Create New Task
                                    </div>
                                    
                                    {/* Task Title */}
                                    <div style={{ marginBottom: '14px' }}>
                                      <label style={{ display: 'block', color: 'white', fontSize: '12px', fontWeight: '500', marginBottom: '6px' }}>
                                        Task Title <span style={{ color: '#FF3B30' }}>*</span>
                                      </label>
                                      <input
                                        type="text"
                                        value={taskFormData.title}
                                        onChange={(e) => setTaskFormData(prev => ({ ...prev, title: e.target.value }))}
                                        disabled={isSubmittingTaskForm}
                                        placeholder="e.g., Follow up with venue"
                                        style={{
                                          width: '100%',
                                          backgroundColor: '#111111',
                                          border: '1px solid #2A2A2A',
                                          borderRadius: '6px',
                                          color: 'white',
                                          fontSize: '13px',
                                          padding: '10px',
                                          outline: 'none',
                                          boxSizing: 'border-box'
                                        }}
                                      />
                                    </div>
                                    
                                    {/* Task Description */}
                                    <div style={{ marginBottom: '14px' }}>
                                      <label style={{ display: 'block', color: 'white', fontSize: '12px', fontWeight: '500', marginBottom: '6px' }}>
                                        Description
                                      </label>
                                      <textarea
                                        value={taskFormData.description}
                                        onChange={(e) => setTaskFormData(prev => ({ ...prev, description: e.target.value }))}
                                        disabled={isSubmittingTaskForm}
                                        placeholder="Add details about this task..."
                                        rows={3}
                                        style={{
                                          width: '100%',
                                          backgroundColor: '#111111',
                                          border: '1px solid #2A2A2A',
                                          borderRadius: '6px',
                                          color: 'white',
                                          fontSize: '13px',
                                          padding: '10px',
                                          outline: 'none',
                                          boxSizing: 'border-box',
                                          resize: 'vertical',
                                          fontFamily: 'inherit'
                                        }}
                                      />
                                    </div>
                                    
                                    {/* Due Date */}
                                    <div style={{ marginBottom: '14px' }}>
                                      <CustomCalendar
                                        value={taskFormData.due_date}
                                        onChange={(value) => setTaskFormData(prev => ({ ...prev, due_date: value }))}
                                        showTime={true}
                                        label="Due Date *"
                                        placeholder="Select due date and time..."
                                        theme="tasks"
                                        required
                                      />
                                    </div>
                                    
                                    {/* Priority */}
                                    <div style={{ marginBottom: '14px' }}>
                                      <label style={{ display: 'block', color: 'white', fontSize: '12px', fontWeight: '500', marginBottom: '6px' }}>
                                        Priority
                                      </label>
                                      <select
                                        value={taskFormData.priority}
                                        onChange={(e) => setTaskFormData(prev => ({ ...prev, priority: e.target.value }))}
                                        disabled={isSubmittingTaskForm}
                                        style={{
                                          width: '100%',
                                          backgroundColor: '#111111',
                                          border: '1px solid #2A2A2A',
                                          borderRadius: '6px',
                                          color: 'white',
                                          fontSize: '13px',
                                          padding: '10px',
                                          outline: 'none',
                                          boxSizing: 'border-box'
                                        }}
                                      >
                                        <option value="low">Low</option>
                                        <option value="medium">Medium</option>
                                        <option value="high">High</option>
                                      </select>
                                    </div>
                                    
                                    {/* Team Member Assignment */}
                                    <div style={{ marginBottom: '14px' }}>
                                      <label style={{ display: 'block', color: 'white', fontSize: '12px', fontWeight: '500', marginBottom: '6px' }}>
                                        Assign to Team Member <span style={{ color: '#FF3B30' }}>*</span>
                                      </label>
                                      {loadingTeam ? (
                                        <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px' }}>Loading team members...</div>
                                      ) : (
                                        <select
                                          value={taskFormData.assignee}
                                          onChange={(e) => setTaskFormData(prev => ({ ...prev, assignee: e.target.value }))}
                                          disabled={isSubmittingTaskForm}
                                          required
                                          style={{
                                            width: '100%',
                                            backgroundColor: '#111111',
                                            border: '1px solid #2A2A2A',
                                            borderRadius: '6px',
                                            color: 'white',
                                            fontSize: '13px',
                                            padding: '10px',
                                            outline: 'none',
                                            boxSizing: 'border-box'
                                          }}
                                        >
                                          <option value="">Select team member...</option>
                                          {teamMembers.map((member) => (
                                            <option key={member.id} value={member.id}>
                                              {member.name || member.username} ({member.role || member.professionRole})
                                            </option>
                                          ))}
                                        </select>
                                      )}
                                    </div>
                                    
                                    {/* Auto Reminder */}
                                    <div style={{ marginBottom: '18px' }}>
                                      <label style={{ display: 'block', color: 'white', fontSize: '12px', fontWeight: '500', marginBottom: '6px' }}>
                                        Auto Reminder
                                      </label>
                                      <select
                                        value={taskFormData.auto_reminder}
                                        onChange={(e) => setTaskFormData(prev => ({ ...prev, auto_reminder: e.target.value }))}
                                        disabled={isSubmittingTaskForm}
                                        style={{
                                          width: '100%',
                                          backgroundColor: '#111111',
                                          border: '1px solid #2A2A2A',
                                          borderRadius: '6px',
                                          color: 'white',
                                          fontSize: '13px',
                                          padding: '10px',
                                          outline: 'none',
                                          boxSizing: 'border-box'
                                        }}
                                      >
                                        <option value="none">No auto reminder</option>
                                        <option value="1_day_before">1 day before</option>
                                        <option value="on_due_date">On due date</option>
                                      </select>
                                    </div>
                                    
                                    {/* Form Buttons */}
                                    <div style={{ display: 'flex', gap: '10px' }}>
                                      <button
                                        onClick={() => {
                                          setTaskFormData({
                                            title: '',
                                            description: '',
                                            due_date: '',
                                            priority: 'medium',
                                            assignee: '',
                                            auto_reminder: 'on_due_date'
                                          });
                                          setTaskFormType(null);
                                        }}
                                        disabled={isSubmittingTaskForm}
                                        style={{
                                          flex: 1,
                                          backgroundColor: 'transparent',
                                          border: '1px solid #2A2A2A',
                                          borderRadius: '6px',
                                          color: '#8E8E93',
                                          fontSize: '13px',
                                          padding: '12px',
                                          cursor: isSubmittingTaskForm ? 'not-allowed' : 'pointer',
                                          fontWeight: '500'
                                        }}
                                      >
                                        Cancel
                                      </button>
                                      <button
                                        onClick={async () => {
                                          if (!taskFormData.title.trim()) {
                                            alert('Please enter a task title');
                                            return;
                                          }

                                          if (!taskFormData.due_date) {
                                            alert('Please select a due date');
                                            return;
                                          }

                                          if (!taskFormData.assignee) {
                                            alert('Please select a team member');
                                            return;
                                          }

                                          setIsSubmittingTaskForm(true);

                                          try {
                                            const token = JSON.parse(localStorage.getItem('dizeeToken'));
                                            
                                            // Find selected team member
                                            const selectedMember = teamMembers.find(member => member.id === taskFormData.assignee);
                                            
                                            // Create task data matching QuickReminder format
                                            const taskData = {
                                              title: taskFormData.title.trim(),
                                              description: taskFormData.description.trim() || `Task assigned to ${selectedMember?.name || selectedMember?.username}`,
                                              dueDate: taskFormData.due_date,
                                              priority: taskFormData.priority,
                                              type: 'reminder',
                                              assigneeId: selectedMember?.id || selectedMember?._id,
                                              metadata: {
                                                assignee_name: selectedMember?.name || selectedMember?.username,
                                                assignee_role: selectedMember?.role || selectedMember?.professionRole,
                                                assignee_email: selectedMember?.email,
                                                auto_reminder: taskFormData.auto_reminder,
                                                quick_reminder: true,
                                                created_from: 'ai_chat'
                                              }
                                            };

                                            const response = await postDataAPI('user/tasks', taskData, token);
                                            
                                            console.log('✅ Task created:', response.data);

                                            // Build summary message
                                            let summaryMessage = `I just created a new task:\n`;
                                            summaryMessage += `• Title: ${taskFormData.title}\n`;
                                            summaryMessage += `• Priority: ${taskFormData.priority}`;
                                            if (taskFormData.description) {
                                              summaryMessage += `\n• Description: ${taskFormData.description}`;
                                            }
                                            if (taskFormData.due_date) {
                                              summaryMessage += `\n• Due Date: ${taskFormData.due_date}`;
                                            }
                                            if (selectedMember) {
                                              summaryMessage += `\n• Assigned to: ${selectedMember.name || selectedMember.username}`;
                                            }

                                            // Clear form
                                            setTaskFormData({
                                              title: '',
                                              description: '',
                                              due_date: '',
                                              priority: 'medium',
                                              assignee: '',
                                              auto_reminder: 'on_due_date'
                                            });
                                            setTaskFormType(null);

                                            // Send summary to AI
                                            window.dispatchEvent(new CustomEvent('sendAIMessage', {
                                              detail: { message: summaryMessage }
                                            }));

                                          } catch (error) {
                                            console.error('Error creating task:', error);
                                            alert(`Error creating task: ${error.response?.data?.msg || error.message || 'Please try again.'}`);
                                          } finally {
                                            setIsSubmittingTaskForm(false);
                                          }
                                        }}
                                        disabled={isSubmittingTaskForm || !taskFormData.title || !taskFormData.due_date || !taskFormData.assignee}
                                        style={{
                                          flex: 1,
                                          backgroundColor: (isSubmittingTaskForm || !taskFormData.title || !taskFormData.due_date || !taskFormData.assignee) ? '#555' : '#FF6B35',
                                          border: 'none',
                                          borderRadius: '6px',
                                          color: 'white',
                                          fontSize: '13px',
                                          padding: '12px',
                                          cursor: isSubmittingTaskForm ? 'not-allowed' : 'pointer',
                                          fontWeight: '600'
                                        }}
                                      >
                                        {isSubmittingTaskForm ? 'Creating...' : 'Create Task'}
                                      </button>
                                    </div>
                                  </div>
                                </>
                              );
                            }

                            // Check if this message contains a confirm shows marker at the beginning
                            // ONLY show confirm shows for ASSISTANT messages, not user messages
                            const hasConfirmShows = m.role === 'assistant' && m.text && m.text.startsWith('[CONFIRM_SHOWS:list]');

                            if (hasConfirmShows) {
                              // Check if shows were already confirmed
                              const nextMessage = chatMessages[i + 1];
                              const showsWereConfirmed = nextMessage &&
                                nextMessage.role === 'user' &&
                                nextMessage.text &&
                                nextMessage.text.toLowerCase().includes('confirm all of these shows');

                              console.log('🔍 [CONFIRM SHOWS DETECTION] Shows confirmed?', showsWereConfirmed);

                              // If shows were already confirmed, just show the AI's intro text
                              if (showsWereConfirmed) {
                                const cleanText = m.text.replace(/^\[CONFIRM_SHOWS:list\]\s*/g, '').trim();

                                return (
                                  <div>
                                    {cleanText.split('\n').map((line, lineIdx) => {
                                      const isBullet = line.trim().startsWith('•');

                                      if (isBullet) {
                                        const text = line.replace('•', '').trim();
                                        return (
                                          <div key={lineIdx}>
                                            <span className="orange-bullet">• </span>
                                            <span>{formatTextWithBold(text)}</span>
                                          </div>
                                        );
                                      }

                                      if (!line.trim()) {
                                        return <div key={lineIdx}>&nbsp;</div>;
                                      }

                                      return <div key={lineIdx}>{formatTextWithBold(line)}</div>;
                                    })}
                                  </div>
                                );
                              }

                              // Shows haven't been confirmed yet, show the list with confirm button
                              const cleanText = m.text.replace(/^\[CONFIRM_SHOWS:list\]\s*/g, '').trim();

                              return (
                                <>
                                  {/* Display the AI's message text */}
                                  {cleanText && (
                                    <div style={{ marginBottom: '16px' }}>
                                      {cleanText.split('\n').map((line, lineIdx) => {
                                        const isBullet = line.trim().startsWith('•');

                                        if (isBullet) {
                                          const text = line.replace('•', '').trim();
                                          return (
                                            <div key={lineIdx}>
                                              <span className="orange-bullet">• </span>
                                              <span>{formatTextWithBold(text)}</span>
                                            </div>
                                          );
                                        }

                                        if (!line.trim()) {
                                          return <div key={lineIdx}>&nbsp;</div>;
                                        }

                                        return <div key={lineIdx}>{formatTextWithBold(line)}</div>;
                                      })}
                                    </div>
                                  )}

                                  {/* Confirm Shows Button */}
                                  <div style={{
                                    backgroundColor: '#1A1A1A',
                                    border: '2px solid #ffff05',
                                    borderRadius: '12px',
                                    padding: '20px',
                                    marginTop: '12px',
                                    width: '100%',
                                    boxSizing: 'border-box'
                                  }}>
                                    <div style={{
                                      color: '#ffff05',
                                      fontSize: '16px',
                                      fontWeight: '600',
                                      marginBottom: '16px',
                                      paddingBottom: '12px',
                                      borderBottom: '1px solid #2A2A2A'
                                    }}>
                                      Confirm All Pending Shows
                                    </div>

                                    <div style={{
                                      color: 'white',
                                      fontSize: '14px',
                                      marginBottom: '18px',
                                      lineHeight: '1.5'
                                    }}>
                                      Click below to confirm all pending shows at once. This will change their status from "pending" to "confirmed".
                                    </div>

                                    {/* Confirm Button */}
                                    <div style={{ display: 'flex', gap: '10px' }}>
                                      <button
                                        onClick={() => {
                                          // Send the confirmation message
                                          const confirmMessage = "confirm all of these shows";
                                          window.dispatchEvent(new CustomEvent('sendAIMessage', {
                                            detail: { message: confirmMessage }
                                          }));
                                        }}
                                        style={{
                                          flex: 1,
                                          backgroundColor: '#ffff05',
                                          border: 'none',
                                          borderRadius: '6px',
                                          color: '#000',
                                          fontSize: '13px',
                                          padding: '12px',
                                          cursor: 'pointer',
                                          fontWeight: '600'
                                        }}
                                      >
                                        Confirm All Shows
                                      </button>
                                    </div>
                                  </div>
                                </>
                              );
                            }

                            // Check if this message contains a confirm shows checklist form marker
                            // ONLY show confirm shows form for ASSISTANT messages, not user messages
                            const hasConfirmShowsForm = m.role === 'assistant' && m.text && m.text.startsWith('[CONFIRM_SHOWS:form]');
                            
                            if (hasConfirmShowsForm) {
                              // Clean text (just like other forms)
                              const cleanText = m.text.replace(/^\[CONFIRM_SHOWS:form\]\s*/g, '').trim();
                              
                              // Check if form was already submitted
                              const nextMessage = chatMessages[i + 1];
                              const formWasSubmitted = nextMessage && 
                                nextMessage.role === 'user' && 
                                nextMessage.text && 
                                nextMessage.text.includes('I confirmed');
                              
                              if (formWasSubmitted) {
                                return (
                                  <div>
                                    {cleanText.split('\n').map((line, lineIdx) => {
                                      if (!line.trim()) return <div key={lineIdx}>&nbsp;</div>;
                                      return <div key={lineIdx}>{formatTextWithBold(line)}</div>;
                                    })}
                                  </div>
                                );
                              }
                              
                              return (
                                <>
                                  {cleanText && (
                                    <div style={{ marginBottom: '16px' }}>
                                      {cleanText.split('\n').map((line, lineIdx) => {
                                        if (!line.trim()) return <div key={lineIdx}>&nbsp;</div>;
                                        return <div key={lineIdx}>{formatTextWithBold(line)}</div>;
                                      })}
                                    </div>
                                  )}
                                  
                                  {/* Confirm Shows Checklist Form */}
                                  <div style={{
                                    backgroundColor: '#000000',
                                    border: '2px solid #ffff05',
                                    borderRadius: '12px',
                                    padding: '20px',
                                    marginTop: '12px',
                                    width: 'calc(100% / 0.75)',
                                    maxWidth: 'none',
                                    boxSizing: 'border-box',
                                    marginLeft: '0',
                                    marginRight: 'calc(-33.33% - 20px)',
                                    position: 'relative'
                                  }}>
                                    <div style={{ 
                                      color: '#ffff05', 
                                      fontSize: '16px', 
                                      fontWeight: '600', 
                                      marginBottom: '16px',
                                      paddingBottom: '12px',
                                      borderBottom: '1px solid #2A2A2A'
                                    }}>
                                      Confirm Pending Shows
                                    </div>
                                    
                                    {/* Show Checklist */}
                                    <div style={{ marginBottom: '18px', maxHeight: '400px', overflowY: 'auto' }}>
                                      {pendingShowsData.length === 0 ? (
                                        <div style={{ color: '#8E8E93', fontSize: '13px', textAlign: 'center', padding: '20px' }}>
                                          Loading pending shows...
                                        </div>
                                      ) : (
                                        pendingShowsData.map((show) => (
                                          <div 
                                            key={show.id}
                                            onClick={() => {
                                              setSelectedShowIds(prev => 
                                                prev.includes(show.id) 
                                                  ? prev.filter(id => id !== show.id)
                                                  : [...prev, show.id]
                                              );
                                            }}
                                            style={{
                                              backgroundColor: selectedShowIds.includes(show.id) ? 'rgba(255, 255, 5, 0.1)' : '#111111',
                                              border: `1px solid ${selectedShowIds.includes(show.id) ? '#ffff05' : '#2A2A2A'}`,
                                              borderRadius: '6px',
                                              padding: '12px',
                                              marginBottom: '8px',
                                              cursor: 'pointer',
                                              display: 'flex',
                                              alignItems: 'center',
                                              gap: '12px',
                                              transition: 'all 0.2s ease'
                                            }}
                                          >
                                            <div style={{
                                              width: '20px',
                                              height: '20px',
                                              borderRadius: '4px',
                                              border: `2px solid ${selectedShowIds.includes(show.id) ? '#ffff05' : '#2A2A2A'}`,
                                              backgroundColor: selectedShowIds.includes(show.id) ? '#ffff05' : 'transparent',
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                              flexShrink: 0
                                            }}>
                                              {selectedShowIds.includes(show.id) && (
                                                <span style={{ color: '#000', fontSize: '14px', fontWeight: 'bold' }}>✓</span>
                                              )}
                                            </div>
                                            <div style={{ flex: 1 }}>
                                              <div style={{ color: 'white', fontSize: '13px', fontWeight: '500', marginBottom: '4px' }}>
                                                {new Date(show.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} - {show.venue}
                                              </div>
                                              <div style={{ color: '#8E8E93', fontSize: '12px' }}>
                                                {show.location} • {show.artist}
                                              </div>
                                            </div>
                                          </div>
                                        ))
                                      )}
                                    </div>
                                    
                                    {/* Action Button */}
                                    <div style={{ display: 'flex' }}>
                                      <button
                                        onClick={async () => {
                                          if (selectedShowIds.length === 0) {
                                            alert('Please select at least one show to confirm');
                                            return;
                                          }
                                          
                                          setIsSubmittingConfirmation(true);
                                          try {
                                            const token = JSON.parse(localStorage.getItem('dizeeToken'));
                                            
                                            // Get full booking data for all bookings first
                                            const response = await getDataAPI('user/getBookings', token);
                                            console.log('📋 [CONFIRM] Response:', response);
                                            
                                            // Extract bookings array using the same logic as fetch pending shows
                                            let allBookings = [];
                                            if (response?.data?.data && typeof response.data.data === 'object') {
                                              if (Array.isArray(response.data.data.bookings)) {
                                                allBookings = response.data.data.bookings;
                                              } else if (Array.isArray(response.data.data.data)) {
                                                allBookings = response.data.data.data;
                                              } else if (response.data.data._id) {
                                                allBookings = [response.data.data];
                                              }
                                            } else if (Array.isArray(response?.data?.data)) {
                                              allBookings = response.data.data;
                                            } else if (Array.isArray(response?.data)) {
                                              allBookings = response.data;
                                            }
                                            
                                            console.log('📋 [CONFIRM] All bookings:', allBookings);
                                            
                                            // Update each selected show's status to 'confirmed'
                                            const updatePromises = selectedShowIds.map(showId => {
                                              const booking = allBookings.find(b => b._id === showId);
                                              if (!booking) {
                                                console.error('Booking not found:', showId);
                                                return Promise.resolve();
                                              }
                                              
                                              console.log('📋 [CONFIRM] Updating booking:', showId, booking);
                                              
                                              return putDataAPI('user/updateBooking', {
                                                bookingId: showId,
                                                data: {
                                                  perf_date: booking.perf_date,
                                                  artist: booking.artist,
                                                  venue: booking.venue,
                                                  city_state: booking.city_state,
                                                  status: 'confirmed'
                                                }
                                              }, token);
                                            });
                                            
                                            await Promise.all(updatePromises);
                                            
                                            // Build summary message
                                            const confirmedShows = pendingShowsData.filter(show => selectedShowIds.includes(show.id));
                                            let summaryMessage = `I confirmed ${confirmedShows.length} show${confirmedShows.length > 1 ? 's' : ''}:\n`;
                                            confirmedShows.forEach(show => {
                                              const formattedDate = new Date(show.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                                              summaryMessage += `• ${formattedDate} - ${show.venue} (${show.location})\n`;
                                            });
                                            
                                            // Clear form state
                                            setPendingShowsData([]);
                                            setSelectedShowIds([]);
                                            
                                            // Send summary to AI
                                            window.dispatchEvent(new CustomEvent('sendAIMessage', {
                                              detail: { message: summaryMessage }
                                            }));
                                          } catch (error) {
                                            console.error('Error confirming shows:', error);
                                            alert(`Error confirming shows: ${error.response?.data?.msg || error.message || 'Please try again.'}`);
                                          } finally {
                                            setIsSubmittingConfirmation(false);
                                          }
                                        }}
                                        disabled={isSubmittingConfirmation || selectedShowIds.length === 0}
                                        style={{
                                          width: '100%',
                                          backgroundColor: (isSubmittingConfirmation || selectedShowIds.length === 0) ? '#555' : '#ffff05',
                                          border: 'none',
                                          borderRadius: '6px',
                                          color: '#000',
                                          fontSize: '13px',
                                          padding: '12px',
                                          cursor: (isSubmittingConfirmation || selectedShowIds.length === 0) ? 'not-allowed' : 'pointer',
                                          fontWeight: '600'
                                        }}
                                      >
                                        {isSubmittingConfirmation ? 'Confirming...' : `Confirm Shows (${selectedShowIds.length})`}
                                      </button>
                                    </div>
                                  </div>
                                </>
                              );
                            }

                            // Check if this message contains navigation shortcuts (General category)
                            // ONLY show navigation boxes for ASSISTANT messages, not user messages
                            const hasNavigation = m.role === 'assistant' && m.text && m.text.startsWith('[NAVIGATE:');
                            
                            if (hasNavigation) {
                              // Extract the navigation target and message text
                              const match = m.text.match(/^\[NAVIGATE:([^\]]+)\](.*)/);
                              if (match) {
                                const [, navTarget, navMessage] = match;
                                console.log('🔗 Navigation detected:', navTarget);
                                
                                // Determine app color and title based on target
                                let appColor = '#007AFF'; // Default blue
                                let appTitle = 'Quick Navigation';
                                
                                if (navTarget.startsWith('tasks')) {
                                  appColor = '#FFA500'; // Orange for Tasks
                                  appTitle = 'Tasks';
                                } else if (navTarget.startsWith('notes')) {
                                  appColor = '#9C6BFF'; // Purple for Notes
                                  appTitle = 'Notes';
                                } else if (navTarget.startsWith('contacts')) {
                                  appColor = '#FF6B6B'; // Red for Contacts
                                  appTitle = 'Contacts';
                                } else if (navTarget.startsWith('connectors')) {
                                  appColor = '#00C853'; // Green for Connectors
                                  appTitle = 'Connectors';
                                }
                                
                                return (
                                  <>
                                    {/* Display the AI's message text if present */}
                                    {navMessage && navMessage.trim() && (
                                      <div style={{ marginBottom: '16px', color: 'rgba(255, 255, 255, 0.85)' }}>
                                        {navMessage.trim().split('\n').map((line, lineIdx) => {
                                          if (!line.trim()) {
                                            return <div key={lineIdx}>&nbsp;</div>;
                                          }
                                          return <div key={lineIdx}>{formatTextWithBold(line)}</div>;
                                        })}
                                      </div>
                                    )}
                                    
                                    {/* Navigation Box */}
                                    <div style={{
                                      backgroundColor: '#1A1A1A',
                                      border: `2px solid ${appColor}`,
                                      borderRadius: '12px',
                                      padding: '20px',
                                      marginTop: '12px',
                                      width: '100%',
                                      boxSizing: 'border-box'
                                    }}>
                                      <div style={{
                                        color: appColor,
                                        fontSize: '16px',
                                        fontWeight: '600',
                                        marginBottom: '16px',
                                        paddingBottom: '12px',
                                        borderBottom: '1px solid #2A2A2A'
                                      }}>
                                        {appTitle}
                                      </div>
                                      
                                      <button
                                        onClick={() => {
                                          console.log('🔗 Navigating to:', navTarget);
                                          navigate(`/${navTarget}`);
                                        }}
                                        style={{
                                          width: '100%',
                                          backgroundColor: appColor,
                                          border: 'none',
                                          borderRadius: '6px',
                                          color: 'white',
                                          fontSize: '13px',
                                          padding: '12px',
                                          cursor: 'pointer',
                                          fontWeight: '600'
                                        }}
                                      >
                                        Go Now
                                      </button>
                                    </div>
                                  </>
                                );
                              }
                            }

                            // Check if this message contains touring navigation markers
                            // ONLY show touring navigation for ASSISTANT messages, not user messages
                            const hasTouringFlights = m.role === 'assistant' && m.text && m.text.startsWith('[TOURING_FLIGHTS:navigate]');
                            const hasTouringHotels = m.role === 'assistant' && m.text && m.text.startsWith('[TOURING_HOTELS:navigate]');
                            const hasTouringTransport = m.role === 'assistant' && m.text && m.text.startsWith('[TOURING_TRANSPORT:navigate]');

                            if (hasTouringFlights || hasTouringHotels || hasTouringTransport) {
                              const cleanText = m.text
                                .replace(/^\[TOURING_FLIGHTS:navigate\]\s*/g, '')
                                .replace(/^\[TOURING_HOTELS:navigate\]\s*/g, '')
                                .replace(/^\[TOURING_TRANSPORT:navigate\]\s*/g, '');

                              let buttonText = '';
                              let buttonMessage = '';

                              if (hasTouringFlights) {
                                buttonText = 'Go to Tour Manager';
                                buttonMessage = 'Go to Tour Manager for flights';
                              } else if (hasTouringHotels) {
                                buttonText = 'Book Travel for Shows';
                                buttonMessage = 'Click "Book travel" on the show you want to find hotels for';
                              } else if (hasTouringTransport) {
                                buttonText = 'Go to Tour Manager';
                                buttonMessage = 'Go to Tour Manager for transportation';
                              }

                              return (
                                <>
                                  {/* Display the AI's message text */}
                                  <div style={{ marginBottom: '16px' }}>
                                    {cleanText}
                                  </div>

                                  {/* Touring Navigation Button */}
                                  <div style={{
                                    backgroundColor: '#1A1A1A',
                                    border: '2px solid #FF9500', // Tour manager orange color
                                    borderRadius: '12px',
                                    padding: '20px',
                                    marginTop: '12px',
                                    width: '100%',
                                    boxSizing: 'border-box'
                                  }}>
                                    <div style={{
                                      color: '#FF9500',
                                      fontSize: '16px',
                                      fontWeight: '600',
                                      marginBottom: '16px',
                                      paddingBottom: '12px',
                                      borderBottom: '1px solid #2A2A2A'
                                    }}>
                                      {hasTouringHotels ? 'Select Show for Hotel Booking' : 'Tour Manager Navigation'}
                                    </div>

                                    <div style={{
                                      color: 'white',
                                      fontSize: '14px',
                                      marginBottom: '18px',
                                      lineHeight: '1.5'
                                    }}>
                                      {hasTouringHotels ? buttonMessage : 'Navigate to Tour Manager to manage your touring logistics.'}
                                    </div>

                                    {/* Navigation Button */}
                                    <div style={{ display: 'flex', gap: '10px' }}>
                                      <button
                                        onClick={() => {
                                          navigate('/tour-manager/view-all');
                                        }}
                                        style={{
                                          flex: 1,
                                          backgroundColor: '#FF9500',
                                          border: 'none',
                                          borderRadius: '6px',
                                          color: '#000',
                                          fontSize: '13px',
                                          padding: '12px',
                                          cursor: 'pointer',
                                          fontWeight: '600'
                                        }}
                                      >
                                        {buttonText}
                                      </button>
                                    </div>
                                  </div>
                                </>
                              );
                            }

                            // Check if this message contains add ticket counts marker
                            // ONLY show ticket counts form for ASSISTANT messages, not user messages
                            const hasAddTicketCounts = m.role === 'assistant' && m.text && m.text.startsWith('[ADD_TICKET_COUNTS:form]');

                            if (hasAddTicketCounts) {
                              const cleanText = m.text.replace(/^\[ADD_TICKET_COUNTS:form\]\s*/, '');
                              const messageText = cleanText;

                              return (
                                <>
                                  {/* Display the AI's message text */}
                                  <div style={{ marginBottom: '16px' }}>
                                    {formatTextWithBold(messageText)}
                                  </div>
                                </>
                              );
                            }

                            // Standard message rendering
                            // Clean any markers before displaying
                            const cleanedText = (m.text || '')
                              .replace(/^\[CATALOG_FORM:(release|demo)\]\s*/g, '')
                              .replace(/^\[BOOKING_FORM:show\]\s*/g, '')
                              .replace(/^\[CONFIRM_SHOWS:list\]\s*/g, '')
                              .replace(/^\[CONFIRM_SHOWS:form\]\s*/g, '')
                              .replace(/^\[TOURING_FLIGHTS:navigate\]\s*/g, '')
                              .replace(/^\[TOURING_HOTELS:navigate\]\s*/g, '')
                              .replace(/^\[TOURING_TRANSPORT:navigate\]\s*/g, '')
                              .replace(/^\[ADD_TICKET_COUNTS:form\]\s*/g, '')
                              .replace(/^\[TASK_FORM:[^\]]+\]\s*/g, '')
                              .replace(/^\[NAVIGATE:[^\]]+\]\s*/g, '');
                            
                            const lines = cleanedText.split('\n');
                            return lines.map((line, lineIdx) => {
                              // Check if line is a markdown header (###)
                              const isMarkdownHeader = line.trim().startsWith('###');
                              
                              if (isMarkdownHeader) {
                                // Remove ### and any trailing colon, trim whitespace
                                const headerText = line.replace(/^#+\s*/, '').replace(/:\s*$/, '').trim();
                                return (
                                  <div key={lineIdx} style={{ marginTop: lineIdx > 0 ? '12px' : '0', marginBottom: '8px' }}>
                                    <span style={{ color: '#FF2300', fontSize: '14px', fontWeight: '600' }}>• </span>
                                    <span style={{ color: 'rgba(255, 255, 255, 0.95)', fontSize: '14px', fontWeight: '600' }}>{formatTextWithBold(headerText)}</span>
                                  </div>
                                );
                              }
                              
                              const isBullet = line.trim().startsWith('•');
                              
                              if (isBullet) {
                                const text = line.replace('•', '').trim();
                                return (
                                  <div key={lineIdx}>
                                    <span className="orange-bullet">• </span>
                                    <span>{formatTextWithBold(text)}</span>
                                  </div>
                                );
                              }
                              
                              // Empty line
                              if (!line.trim()) {
                                return <div key={lineIdx}>&nbsp;</div>;
                              }
                              
                              return <div key={lineIdx}>{formatTextWithBold(line)}</div>;
                            });
                          })()}
                          
                          {/* Tour options - render inline if this is a tour message */}
                          {m.isTourMessage && (m.showOptions && m.options || m.buttons) && (
                            <div style={{ marginTop: '16px' }}>
                              <TourOptions 
                                options={m.options}
                                buttons={m.buttons}
                                onOptionSelect={handleTourOptionSelect}
                                onButtonAction={handleOnboardingButtonAction}
                              />
                            </div>
                          )}
                          
                          {/* Tour preset questions - render inline if requested */}
                          {m.isTourMessage && m.showQuestions && (
                            <div style={{ marginTop: '12px' }}>
                              <TourPresetQuestions 
                                onQuestionSelect={handleTourQuestionSelect}
                              />
                            </div>
                          )}
                        </MessageComponent>
                      );
                    })}
                    
                    {/* Thinking indicator */}
                    {isThinking && !typingBuffer && (
                      <ThinkingMessage
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        <span>Thinking</span>
                        <span className="dot">.</span>
                        <span className="dot">.</span>
                        <span className="dot">.</span>
                      </ThinkingMessage>
                    )}
                    
                    {/* Typing buffer with orange highlight */}
                    {typingBuffer && typingBuffer.length > 0 && (
                      <AIMessage 
                        role="assistant"
                        initial={{ opacity: 1, y: 0 }}
                        animate={{ opacity: 1, y: 0 }}
                      >
                        {(() => {
                          // Strip all markers from typing buffer (same as default message rendering)
                          const buf = typingBuffer
                            .replace(/^\[CATALOG_FORM:(release|demo)\]\s*/g, '')
                            .replace(/^\[BOOKING_FORM:show\]\s*/g, '')
                            .replace(/^\[CONFIRM_SHOWS:list\]\s*/g, '')
                            .replace(/^\[CONFIRM_SHOWS:form\]\s*/g, '')
                            .replace(/^\[TOURING_FLIGHTS:navigate\]\s*/g, '')
                            .replace(/^\[TOURING_HOTELS:navigate\]\s*/g, '')
                            .replace(/^\[TOURING_TRANSPORT:navigate\]\s*/g, '')
                            .replace(/^\[ADD_TICKET_COUNTS:form\]\s*/g, '')
                            .replace(/^\[TASK_FORM:[^\]]+\]\s*/g, '')
                            .replace(/^\[NAVIGATE:[^\]]+\]\s*/g, '');
                          const lines = buf.split('\n');
                          
                          // Format text with orange bullets and bold support - same structure as final render
                          return lines.map((line, lineIdx) => {
                            // Check if line is a markdown header (###)
                            const isMarkdownHeader = line.trim().startsWith('###');
                            
                            if (isMarkdownHeader) {
                              // Remove ### and any trailing colon, trim whitespace
                              const headerText = line.replace(/^#+\s*/, '').replace(/:\s*$/, '').trim();
                              return (
                                <div key={lineIdx} style={{ marginTop: lineIdx > 0 ? '12px' : '0', marginBottom: '8px' }}>
                                  <span style={{ color: '#FF2300', fontSize: '14px', fontWeight: '600' }}>• </span>
                                  <span style={{ color: 'rgba(255, 255, 255, 0.95)', fontSize: '14px', fontWeight: '600' }}>{formatTextWithBold(headerText)}</span>
                                </div>
                              );
                            }
                            
                            const isBullet = line.trim().startsWith('•');
                            
                            if (isBullet) {
                              const text = line.replace('•', '').trim();
                              return (
                                <div key={lineIdx}>
                                  <span className="orange-bullet">• </span>
                                  <span>{formatTextWithBold(text)}</span>
                                </div>
                              );
                            }
                            
                            // Empty line
                            if (!line.trim()) {
                              return <div key={lineIdx}>&nbsp;</div>;
                            }
                            
                            return <div key={lineIdx}>{formatTextWithBold(line)}</div>;
                          });
                        })()}
                      </AIMessage>
                    )}
                    
                    {/* Ticket Counts Form - Rendered inside conversation */}
                    {showTicketCountsForm && (
                      <div style={{ marginTop: '20px', marginBottom: '20px' }}>
                    {/* Add Ticket Counts Form - One form per show */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                      {isLoadingConfirmedShows ? (
                        <div style={{
                          backgroundColor: '#000000',
                          border: '2px solid #ffff05',
                          borderRadius: '12px',
                          padding: '40px',
                          textAlign: 'center',
                          color: 'white'
                        }}>
                          Loading confirmed shows...
                        </div>
                      ) : confirmedShows.length === 0 ? (
                        <div style={{
                          backgroundColor: '#000000',
                          border: '2px solid #ffff05',
                          borderRadius: '12px',
                          padding: '40px',
                          textAlign: 'center',
                          color: 'white'
                        }}>
                          No confirmed shows found. Please confirm some shows first in the Bookings app.
                        </div>
                      ) : (
                        confirmedShows.map((show, index) => (
                        <div key={show.id} style={{
                          backgroundColor: '#000000',
                          border: '2px solid #ffff05', // Bookings yellow color
                          borderRadius: '12px',
                          padding: '20px',
                          width: '100%',
                          boxSizing: 'border-box'
                        }}>
                          {/* Show Header */}
                          <div style={{
                            color: '#ffff05',
                            fontSize: '16px',
                            fontWeight: '600',
                            marginBottom: '16px',
                            paddingBottom: '12px',
                            borderBottom: '1px solid #2A2A2A'
                          }}>
                            {(() => {
                              // Format date to readable format
                              try {
                                const date = new Date(show.date);
                                const formattedDate = date.toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric'
                                });
                                return `${formattedDate} - ${show.venue} (${show.location})`;
                              } catch (e) {
                                return `${show.date} - ${show.venue} (${show.location})`;
                              }
                            })()}
                          </div>

                          {/* Ticket Count Input */}
                          <div style={{ marginBottom: '14px' }}>
                            <label style={{ display: 'block', color: 'white', fontSize: '12px', fontWeight: '500', marginBottom: '6px' }}>
                              Tickets Sold
                            </label>
                            <input
                              type="number"
                              placeholder="e.g., 250"
                              defaultValue={show.tix_sold || ''}
                              data-show-id={show.id}
                              data-field="tix_sold"
                              style={{
                                width: '100%',
                                backgroundColor: '#111111',
                                border: '1px solid #2A2A2A',
                                borderRadius: '6px',
                                color: 'white',
                                fontSize: '13px',
                                padding: '10px',
                                outline: 'none',
                                boxSizing: 'border-box'
                              }}
                            />
                          </div>

                          {/* Capacity Input */}
                          <div style={{ marginBottom: '18px' }}>
                            <label style={{ display: 'block', color: 'white', fontSize: '12px', fontWeight: '500', marginBottom: '6px' }}>
                              Sellable Capacity
                            </label>
                            <input
                              type="number"
                              placeholder="e.g., 500"
                              defaultValue={show.sellable_cap || ''}
                              data-show-id={show.id}
                              data-field="sellable_cap"
                              style={{
                                width: '100%',
                                backgroundColor: '#111111',
                                border: '1px solid #2A2A2A',
                                borderRadius: '6px',
                                color: 'white',
                                fontSize: '13px',
                                padding: '10px',
                                outline: 'none',
                                boxSizing: 'border-box'
                              }}
                            />
                          </div>
                        </div>
                        ))
                      )}

                      {/* Form Buttons - Only show once at the bottom */}
                      {confirmedShows.length > 0 && (
                        <div style={{
                          display: 'flex',
                          gap: '10px',
                          marginTop: '16px',
                          justifyContent: 'flex-end'
                        }}>
                        <button
                          style={{
                            backgroundColor: '#ffff05',
                            border: 'none',
                            borderRadius: '6px',
                            color: '#000000',
                            fontSize: '13px',
                            fontWeight: '600',
                            padding: '12px 24px',
                            cursor: 'pointer',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = '#e6e600';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = '#ffff05';
                          }}
                          onClick={async () => {
                            // Collect all form data
                            const ticketUpdates = [];
                            const inputs = document.querySelectorAll('[data-show-id]');
                            const showsMap = {};

                            inputs.forEach(input => {
                              const showId = input.getAttribute('data-show-id');
                              const field = input.getAttribute('data-field');
                              const value = input.value;

                              if (!showsMap[showId]) {
                                showsMap[showId] = {};
                              }
                              showsMap[showId][field] = value;
                            });

                            // Convert to array format expected by backend
                            Object.keys(showsMap).forEach(showId => {
                              ticketUpdates.push({
                                showId,
                                ...showsMap[showId]
                              });
                            });

                            console.log('🎫 Form data to submit:', ticketUpdates);

                            try {
                              const token = localStorage.getItem('dizeeToken');
                              const response = await putDataAPI(
                                'user/bulkUpdateTicketCounts',
                                { ticketUpdates },
                                token
                              );

                              console.log('✅ Ticket counts update response:', response);

                              // Check if the request was successful
                              const isSuccess = response.data?.success || response.status === 200;
                              
                              if (isSuccess) {
                                const data = response.data?.data;
                                const updated = data?.updated || 0;
                                const skipped = data?.skipped || 0;
                                const errors = data?.errors || 0;
                                
                                // Show success message
                                alert(`✅ Ticket counts updated successfully!\n\nShows Updated: ${updated}\nShows Skipped: ${skipped}\nShows with Errors: ${errors}`);
                                
                                // Reset cache to ensure fresh data on next load
                                resetConfirmedShowsCache();
                                // Close the form
                                setShowTicketCountsForm(false);
                              } else {
                                alert(`❌ Error updating ticket counts: ${response.data?.message || 'Unknown error'}`);
                              }
                            } catch (error) {
                              console.error('❌ Error submitting ticket counts:', error);
                              console.error('Error details:', error.response?.data || error.message);
                              alert('Failed to update ticket counts. Please try again.');
                            }
                          }}
                        >
                          Save All Ticket Counts
                        </button>
                        </div>
                      )}
                    </div>
                  </div>
                    )}

                    {/* Spacer to ensure last message is visible above input area */}
                    <ChatSpacer isMaximized={isChatMaximized} />
                  </ConversationArea>
              </ChatContainer>
            </>
          )}
          
          {/* Apps Section - Only shown when activeTab is 'apps' */}
          {activeTab === 'apps' && (
          <AppsSection>
            {/* Expanded View */}
            {isAppsExpanded ? (
              <AppsExpanded />
            ) : (
              /* Simplified Grid View */
              <AppsContainer>
                <AppsScrollContainer ref={appsScrollRef}>
                  {apps.map((app) => (
                    <PermissionWrapper key={app.id} appName={app.id}>
                      <AppWrapper>
                        <AppIcon
                          className={`${app.id}-app-icon`}
                          color={app.color}
                          onClick={app.onClick}
                        />
                        <AppLabel>{app.name}</AppLabel>
                      </AppWrapper>
                    </PermissionWrapper>
                  ))}
                </AppsScrollContainer>
              </AppsContainer>
            )}
          </AppsSection>
          )}

          {/* Activity Feed - Only shown when activeTab is 'activity' */}
          {activeTab === 'activity' && (
            <div 
              className="w-full px-4"
              style={{
                flex: '1 1 0',
                minHeight: 0,
                overflowY: 'auto',
                overflowX: 'hidden',
                WebkitOverflowScrolling: 'touch',
                scrollbarWidth: 'none',
                msOverflowStyle: 'none'
              }}
            >
              <style>{`
                div::-webkit-scrollbar {
                  display: none;
                }
              `}</style>
            <DashboardStats isCollapsed={false} />
            </div>
          )}

          {/* Chat is always handled by FooterBar, no content needed here */}
        </OptionsContainer>
      </MainContent>

      {/* Footer */}
      <FooterBar 
        isHome={true} 
        isVisible={true}
        activeTab={activeTab}
        activeNavItem={activeNavItem}
        onCategoryChange={handleCategoryChange}
        chatMessages={chatMessages}
        setChatMessages={setChatMessages}
        isTyping={isTyping}
        setIsTyping={setIsTyping}
        typingBuffer={typingBuffer}
        setTypingBuffer={setTypingBuffer}
        isThinking={isThinking}
        setIsThinking={setIsThinking}
        isChatMaximized={isChatMaximized}
        onMaximizeToggle={handleMaximizeChat}
        onMinimize={handleMinimizeChat}
        onMessageSent={handleMessageSent}
        onNewChat={handleNewChat}
      />

      {showWalkthrough && <DizeeWalkthrough onClose={handleWalkthroughClose} />}
      {showTutorial && <DizeeTutorial onClose={handleTutorialClose} startAtStep={tutorialStep} />}
    </Container>
  );
};

export default Landing; 